/*
 * DML · Letters from Santa · builder (dml-santa-builder.js)
 * ================================================================
 * The Santa letter builder, built on the Tooth Fairy builder's look
 * (dml-builder.css) plus dml-santa.css. Mounted by
 * sections/dml-santa-desk.liquid, which sets window.DML_SANTA.
 * Steps: Language · Letter · Envelope · Review, then "One last read".
 * One basket line per set of letters (one child, or siblings together):
 *   variants: Early December / Christmas Morning / The Christmas Pair
 *   _dml_state  everything needed to reopen the set ("Edit letter")
 *   _print      the finished text of each letter and envelope, exactly
 *               as previewed, for the Photoshop print data
 *   _Letter I / _Letter II  the same text, readable in the order page
 * The writing helper calls <worker URL>/santa (see worker.js).
 * Unfinished letters are kept in this browser for 30 days (localStorage
 * "dml_santa_draft_v1") and removed once added to the basket.
 * ================================================================
 */
(function () {
  'use strict';
  if (window.customElements && customElements.get('dml-santa-builder')) return;
  if (!window.DML_SANTA_TEXTS) { console.error('[DML Santa] dml-santa-texts.js must load first'); return; }
  var TX = window.DML_SANTA_TEXTS || {};
  var LETTERS = TX.LETTERS, FRAME = TX.FRAME, UI = TX.UI, FIELDS = TX.FIELDS, MUSE = TX.MUSE, LIMITS = TX.LIMITS;
  // =====================================================================
  // Helpers
  // =====================================================================
  var CFG = window.DML_SANTA || {};
  var LANGS = [
    ['english', 'English', 'My dear …,'],
    ['french', 'Français', 'Mon cher … · Ma chère …,'],
    ['german', 'Deutsch', 'Mein lieber … · Meine liebe …,']
  ];
  var LOCALE = { english: 'en-GB', french: 'fr-FR', german: 'de-DE' };
  var DRAFT_KEY = 'dml_santa_draft_v1', DRAFT_DAYS = 30;
  var ORDER = ['language', 'letters', 'envelope', 'review'];
  var LETTER_FIELDS = {
    early: ['names', 'grown', 'outing', 'milestone', 'location', 'way', 'hazard', 'treat', 'habit', 'ps', 'own'],
    morning: ['names', 'grown', 'way', 'treat', 'past', 'growth', 'place', 'ps', 'own']
  };
  var AI_FIELDS = ['outing', 'milestone', 'hazard', 'habit', 'past', 'growth', 'ps', 'own'];

  function esc(t) { return String(t == null ? '' : t).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function clone(o) { return JSON.parse(JSON.stringify(o)); }
  function trim(v) { return String(v || '').trim(); }
  function clean(v) { return trim(v).replace(/[.\s]+$/, ''); }

  function newSet() {
    return {
      choice: 'pair', mode: { early: 'santa', morning: 'santa' }, gender: 'masc', slip: true,
      early: { names: '', grown: '', outing: '', milestone: '', location: '', way: '', hazard: '', treat: '', habit: '', ps: '', own: '' },
      morning: { names: '', grown: '', way: '', treat: '', past: '', growth: '', place: '', ps: '', own: '' },
      links: { names: true, grown: true, way: true, treat: true },
      env: { name: '', addr: '', city: '' }, env2: { name: '', addr: '', city: '' },
      envLinks: { name: true, addr: true, city: true }, nameLinked: true, envDiffer: false, editKey: null,
      count: { kids: 'auto', grown: 'auto' }
    };
  }
  function fixSet(s) {
    var d = newSet();
    Object.keys(d).forEach(function (k) {
      if (s[k] === undefined) s[k] = d[k];
      else if (d[k] && typeof d[k] === 'object' && !Array.isArray(d[k])) s[k] = Object.assign({}, d[k], s[k]);
    });
    return s;
  }

  // =====================================================================
  // The element
  // =====================================================================
  class SantaBuilder extends HTMLElement {}

  SantaBuilder.prototype.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;
    var self = this;
    this.root = this.attachShadow({ mode: 'open' });
    this.state = {
      step: 'language', language: CFG.language || 'english', font: 'classic',
      sets: [newSet()], cur: 0, active: 'early', gift: { on: false, note: '' }
    };
    this.ui = { muse: {}, prompts: {}, gen: '', aiErr: {}, armed: '', proof: false, confirm: false, reader: null, fills: {}, adding: false, err: '', basket: [], loading: false };
    var editKey = null;
    try { editKey = new URLSearchParams(window.location.search).get('dml_edit_key'); } catch (e) {}
    if (editKey) { this.ui.loading = true; this.loadEdit(editKey); }
    else { this.restoreDraft(); this.fromTeaser(); }
    this.root.innerHTML =
      '<link rel="stylesheet" href="' + esc(CFG.tfCss || '') + '">' +
      '<link rel="stylesheet" href="' + esc(CFG.css || '') + '">' +
      '<div class="shell dml-builder-section" id="shell"></div>';
    this.shell = this.root.getElementById('shell');
    this.bind();
    this.render();
    this.loadBasket();
    this.loadGift();
    window.addEventListener('pagehide', function () { self.saveDraft(true); });
    window.addEventListener('resize', function () { self.stick(); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.measure(); });
    this.checkFonts();
  };

  var P = SantaBuilder.prototype;

  // The print fonts come from the Adobe Fonts web project (use.typekit.net/ira7ifq.css, loaded in theme.liquid).
  // Script: American Scribe + Bimbo Pro Regular for the elves. Classic: Geographica + Providence Pro.
  P.checkFonts = function () {
    var self = this, fams = { 'american-scribe': 'nf-script', 'geographica': 'nf-classic', 'p22-cezanne-pro': 'nf-cezanne', 'bimbo-pro': 'nf-elf-script', 'ff-providence-web-pro': 'nf-elf-classic' };
    var weight = {};
    if (!document.fonts || !document.fonts.load) return;
    Promise.all(Object.keys(fams).map(function (f) {
      return document.fonts.load((weight[f] || '') + '16px "' + f + '"').then(function (list) { return [f, list && list.length > 0]; }, function () { return [f, false]; });
    })).then(function (res) {
      self.fontsMissing = [];
      res.forEach(function (r) { if (!r[1]) self.fontsMissing.push(r[0]); if (self.shell) self.shell.classList.toggle(fams[r[0]], !r[1]); });
      self.measure();
      // the kit's stylesheet can arrive after the builder starts: look once more when the page has loaded
      if (self.fontsMissing.length && !self._fontRetry) {
        self._fontRetry = true;
        var again = function () { setTimeout(function () { self.checkFonts(); }, 400); };
        if (document.readyState === 'complete') again(); else window.addEventListener('load', again, { once: true });
      }
    });
  };

  // ---------- state accessors ----------
  P.L = function () { return this.state.language; };
  P.t = function () { return UI[this.L()] || UI.english; };
  P.fl = function (k) { return (FIELDS[this.L()] || FIELDS.english)[k]; };
  P.lim = function (k) { var l = LIMITS[k] || [80, 90]; return this.L() === 'english' ? l[0] : l[1]; };
  P.set = function () { return this.state.sets[this.state.cur]; };
  P.picked = function (s) { return s.choice === 'pair' ? ['early', 'morning'] : [s.choice]; };
  P.activeLetter = function () { var s = this.set(), p = this.picked(s); return p.indexOf(this.state.active) >= 0 ? this.state.active : p[0]; };
  P.val = function (s, L, k) {
    if (L === 'morning' && s.choice === 'pair' && s.links[k] !== undefined && s.links[k]) return s.early[k] || '';
    return (s[L] && s[L][k]) || '';
  };
  P.setVal = function (s, L, k, v) {
    if (L === 'morning' && s.choice === 'pair' && s.links[k] !== undefined && s.links[k]) {
      if (v === s.early[k]) return;
      s.links[k] = false;
    }
    s[L][k] = v;
  };
  // One or several? Read from what the parent typed, unless they chose it themselves (s.count)
  var PLURAL = /\s(and|et|und|y|e|plus)\s|&|,|\+|\//i;
  var PLURAL_WORDS = /(^|[\s'’])(parents|grandparents|grand-parents|grands-parents|mums|moms|mommies|mummies|mothers|dads|daddies|fathers|papas|mamans|aunts|aunties|uncles|tantes|oncles|grandmas|grandpas|grannies|nans|nannas|family|families|folks|everyone|twins|kids|children|boys|girls|famille|enfants|eltern|großeltern|grosseltern|familie|kinder|geschwister|zwillinge)([\s.,!]|$)/i;
  P.autoMany = function (v) { v = trim(v); return PLURAL.test(' ' + v + ' ') || PLURAL_WORDS.test(v); };
  // one child or several: read from the names typed ("Anna and Ben", "Mia, Leo")
  P.isSib = function (s, L) {
    return this.autoMany(this.val(s, L, 'names'));
  };
  // one grown-up or several: read from what was typed ("Mom and Dad", "Grandma", "my parents")
  P.isMany = function (s, L) {
    var g = trim(this.val(s, L, 'grown'));
    if (!g) return true;
    return this.autoMany(g);
  };
  // "your Mum" typed into "for your ___" would read "your your Mum": drop a leading possessive (English)
  P.fillVal = function (s, L, k) {
    var v = k === 'habit' ? trim(this.val(s, L, k)) : clean(this.val(s, L, k));
    if (k === 'grown' && this.L() === 'english') v = v.replace(/^(your|my|our|the)\s+/i, '');
    return v;
  };
  P.gram = function (s, L) {
    // French "de" before the grown-ups' name(s): "d’Anne", "de Papa et Maman"
    var dg = /^[aeiouyhàâéèêëîïôöûüœæ]/i.test(this.fillVal(s, L, 'grown')) ? 'd’' : 'de ';
    return { sib: this.isSib(s, L), many: this.isMany(s, L), fem: s.gender === 'fem' && this.L() !== 'english', neu: s.gender === 'neu' && this.L() !== 'english', dg: dg };
  };
  P.childNames = function (s) { return trim(this.val(s, this.picked(s)[0], 'names')); };

  P.envVal = function (s, which, k) {
    if (which === 2 && s.envDiffer) return s.envLinks[k] ? this.envVal(s, 1, k) : (s.env2[k] || '');
    if (k === 'name' && s.nameLinked) return this.childNames(s);
    return s.env[k] || '';
  };

  // ---------- money ----------
  P.money = function (cents) {
    var cur = CFG.currency || 'EUR', v = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(LOCALE[this.L()] || 'en-GB', { style: 'currency', currency: cur, minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }).format(v);
    } catch (e) { return cur + ' ' + v.toFixed(2); }
  };
  P.price = function (choice) { return (CFG.prices && CFG.prices[choice]) || 0; };

  // =====================================================================
  // Letter content
  // =====================================================================
  P.fr = function (t) {
    if (this.L() !== 'french') return t;
    return String(t).replace(/ ([:;!?\u00BB])/g, '\u00A0$1').replace(/\u00AB /g, '\u00AB\u00A0');
  };
  // Structured letter: used for the preview AND for the print data
  P.letterData = function (s, L) {
    var self = this, lang = this.L(), g = this.gram(s, L), fr = FRAME[lang];
    var seg = function (t) { return { t: self.fr(t) }; };
    var fseg = function (k) {
      var v = self.fillVal(s, L, k);
      return v ? { t: self.fr(v), fill: true, k: k } : { t: '[' + self.fl(k === 'way' && L === 'morning' ? 'way2' : (k === 'treat' && L === 'morning' ? 'treat2' : k))[0].toLowerCase() + ']', ph: true, k: k };
    };
    var names = clean(this.val(s, L, 'names'));
    var d = {
      greeting: [seg(fr.hello(g)), names ? { t: names, fill: true, k: 'names' } : { t: '[' + this.fl('names')[0].split(' (')[0].toLowerCase() + ']', ph: true, k: 'names' }, seg(fr.comma)],
      paras: [], slip: null, slipAt: -1,
      closing: fr.close[L].map(function (c) { return self.fr(c); }), sign: fr.sign(g), elf: fr.elf, ps: null
    };
    var slipLines = function () {
      return fr.slip.map(function (x) {
        var v = trim(self.val(s, 'early', x[0]));
        if (x[0] !== 'habit') v = clean(v);
        return { k: x[0], label: self.fr(x[2] && !g.many ? x[2] : x[1]), v: v ? self.fr(v) : '', ph: !v, phText: '[' + self.fl(x[0])[0].toLowerCase() + ']' };
      });
    };
    if (s.mode[L] === 'own') {
      d.greeting = null;
      var body = trim(s[L].own), token = '[' + this.t().elves_t + ']';
      if (!body) d.paras.push([{ t: '[' + this.fl('own')[0].toLowerCase() + ']', ph: true }]);
      else body.split(/\n\s*\n/).forEach(function (p) {
        p = trim(p);
        if (!p) return;
        if (p === token) { if (L === 'early' && s.slip && d.slipAt < 0) { d.slipAt = d.paras.length; d.slip = slipLines(); } return; }
        d.paras.push([seg(p)]);
      });
      if (L === 'early' && s.slip && d.slipAt < 0) { d.slipAt = d.paras.length; d.slip = slipLines(); }
    } else {
      (LETTERS[lang] || LETTERS.english)[L](g).forEach(function (p) {
        if (p === 'SLIP') { d.slipAt = d.paras.length; d.slip = slipLines(); return; }
        d.paras.push(p.map(function (x) { return typeof x === 'string' ? seg(x) : fseg(x.f); }));
      });
    }
    var ps = trim(s[L].ps);
    if (ps) d.ps = [seg(fr.ps), { t: this.fr(ps), fill: true, k: 'ps' }];
    return d;
  };
  P.segsHTML = function (segs, hl) {
    return segs.map(function (x) {
      var dk = x.k ? ' data-k="' + x.k + '"' : '';
      if (x.ph) return '<span class="ph"' + dk + '>' + esc(x.t) + '</span>';
      if (x.fill && hl) return '<mark class="fill"' + dk + '>' + esc(x.t) + '</mark>';
      return esc(x.t);
    }).join('');
  };
  P.letterHTML = function (s, L, hl) {
    var self = this, d = this.letterData(s, L), h = d.greeting ? '<p class="lp">' + this.segsHTML(d.greeting, hl) + '</p>' : '';
    var slipHTML = function () {
      return '<div class="slipbox">' + d.slip.map(function (x) {
        return '<span class="sline" data-k="' + x.k + '"><span class="sl">- ' + esc(x.label) + '</span> ' + (x.ph ? '<span class="sfe">' + esc(x.phText) + '</span>' : (hl ? '<mark class="fill">' + esc(x.v) + '</mark>' : esc(x.v))) + '</span>';
      }).join('') + '</div>';
    };
    d.paras.forEach(function (p, i) {
      if (d.slip && d.slipAt === i) h += slipHTML();
      h += '<p class="lp">' + self.segsHTML(p, hl) + '</p>';
    });
    if (d.slip && d.slipAt >= d.paras.length) h += slipHTML();
    h += '<div class="closing">' + d.closing.map(function (c) { return '<span class="cl">' + esc(c) + '</span>'; }).join('') + '<span class="cl sig">' + esc(d.sign) + '</span></div>';
    if (d.ps) h += '<p class="lp ps">' + this.segsHTML(d.ps, hl) + '</p>';
    return h;
  };
  P.letterText = function (s, L) {
    var d = this.letterData(s, L), txt = function (segs) { return segs.map(function (x) { return x.t; }).join(''); };
    var out = d.greeting ? [txt(d.greeting)] : [];
    d.paras.forEach(function (p, i) {
      if (d.slip && d.slipAt === i) out.push(d.slip.map(function (x) { return '- ' + x.label + ' ' + x.v; }).join('\n'));
      out.push(txt(p));
    });
    if (d.slip && d.slipAt >= d.paras.length) out.push(d.slip.map(function (x) { return '- ' + x.label + ' ' + x.v; }).join('\n'));
    out.push(d.closing.concat([d.sign]).join('\n'));
    if (d.ps) out.push(txt(d.ps));
    return out.join('\n\n');
  };
  // Santa's words with the parent's details, to start the own-words editor from
  P.plainLetter = function (s, L) {
    var self = this, lang = this.L(), token = '[' + this.t().elves_t + ']';
    return [this.greetingLine(s, L)].concat((LETTERS[lang] || LETTERS.english)[L](this.gram(s, L)).map(function (p) {
      if (p === 'SLIP') return token;
      return p.map(function (x) {
        if (typeof x === 'string') return x;
        return self.fillVal(s, L, x.f) || '…';
      }).join('');
    })).join('\n\n');
  };
  // "My dear Elliot," in the letter's language and grammar (name may be a placeholder)
  P.greetingLine = function (s, L, name) {
    var fr = FRAME[this.L()], nm = name != null ? name : clean(this.val(s, L, 'names'));
    return this.fr(fr.hello(this.gram(s, L)) + (nm || '…') + fr.comma);
  };
  // The sentence around a blank, for the writing helper
  // The writing helper sometimes repeats the words just before the blank ("Le dernier chapitre date de l'an
  // dernier, quand vous faisiez..."): keep only what goes in the blank.
  P.stripEcho = function (txt, sentence) {
    if (!txt || !sentence || sentence.indexOf('___') < 0) return txt;
    var norm = function (w) { return w.toLowerCase().replace(/[\u2019\u2018`]/g, "'").replace(/^[\u00AB\u00BB\u201C\u201D\u201E"(]+|[\u00AB\u00BB\u201C\u201D\u201E",;:.)]+$/g, ''); };
    var before = trim(sentence.split('___')[0]).split(/\s+/).filter(Boolean).map(norm);
    var words = trim(txt).split(/\s+/);
    for (var n = Math.min(before.length, words.length - 1); n >= 1; n--) {
      var ok = true;
      for (var i = 0; i < n; i++) if (norm(words[i]) !== before[before.length - n + i]) { ok = false; break; }
      if (ok) return trim(words.slice(n).join(' ').replace(/^[,;:\s]+/, ''));
    }
    return txt;
  };
  P.sentenceFor = function (s, L, k) {
    var self = this, lang = this.L();
    if (k === 'hazard' || k === 'habit') {
      var lab = FRAME[lang].slip.filter(function (x) { return x[0] === k; })[0], g1 = this.gram(s, L);
      return lab ? (lab[2] && !g1.many ? lab[2] : lab[1]) + ' ___' : '';
    }
    var paras = (LETTERS[lang] || LETTERS.english)[L](this.gram(s, L));
    for (var i = 0; i < paras.length; i++) {
      var p = paras[i];
      if (p === 'SLIP') continue;
      if (!p.some(function (x) { return x && x.f === k; })) continue;
      var txt = p.map(function (x) {
        if (typeof x === 'string') return x;
        return x.f === k ? '___' : (self.fillVal(s, L, x.f) || '…');
      }).join('');
      var m = txt.match(/[^.!?]*___[^.!?]*[.!?]?/);
      return trim(m ? m[0] : txt).slice(0, 380);
    }
    return '';
  };

  // =====================================================================
  // Envelopes
  // =====================================================================
  P.envOf = function (s, L) {
    var which = s.choice === 'pair' && L === 'morning' ? 2 : 1, t = this.t();
    var name = trim(this.envVal(s, which, 'name')), addr = trim(this.envVal(s, which, 'addr')), city = trim(this.envVal(s, which, 'city'));
    var lines = [], phLines = false;
    if (addr) lines.push(addr);
    if (city) lines.push(city);
    if (!lines.length) { lines = [this.fl('env_addr')[2], this.fl('env_city')[2]]; phLines = true; }
    return { name: name || this.fl('env_name')[2], phName: !name, lines: lines, phLines: phLines, L: L };
  };
  P.envHTML = function (e, cls) {
    return '<div class="frame envelope-frame ' + (cls || '') + '">' +
      '<div class="env-text"><div class="env-name' + (e.phName ? ' is-ph' : '') + '">' + esc(e.name) + '</div><div class="env-address' + (e.phLines ? ' is-ph' : '') + '">' +
      e.lines.map(function (l) { return '<div>' + esc(l) + '</div>'; }).join('') + '</div></div></div>';
  };

  // =====================================================================
  // Missing details
  // =====================================================================
  // The German Letter I has no paragraph about the grown-ups, so it does not ask for their names
  P.usesGrown = function (L) { return !(L === 'early' && this.L() === 'german'); };
  P.missingFor = function (s, L) {
    var self = this, req = [['names']];
    var slipReq = [['location'], ['way'], ['hazard'], ['treat'], ['habit']];
    if (s.mode[L] === 'own') { req.push(['own']); if (L === 'early' && s.slip) req = req.concat(slipReq); }
    else if (L === 'early') req = req.concat([['outing'], ['milestone']], slipReq, this.usesGrown(L) ? [['grown']] : []);
    else req = req.concat([['way'], ['treat'], ['past'], ['growth'], ['grown'], ['place']]);
    var out = req.filter(function (r) { return !trim(self.val(s, L, r[0])); }).map(function (r) {
      var key = r[0];
      if (L === 'morning' && key === 'way') key = 'way2';
      if (L === 'morning' && key === 'treat') key = 'treat2';
      return { label: self.fl(key)[0], L: L, step: 'letters', field: r[0] };
    });
    if (s.mode[L] === 'own' && (this.ui.fills[this.state.sets.indexOf(s) + L] || 0) > 1.001) out.push({ label: this.t().too_long.split('.')[0], L: L, step: 'letters' });
    return out;
  };
  P.missingEnv = function (s, L) {
    var which = s.choice === 'pair' && L === 'morning' ? 2 : 1;
    var out = trim(this.envVal(s, which, 'name')) ? [] : [{ label: this.t().env_name, L: L, step: 'envelope', field: 'name', env: which }];
    return out;
  };
  P.allMissing = function (s) {
    var self = this, out = [];
    this.picked(s).forEach(function (L) { out = out.concat(self.missingFor(s, L), self.missingEnv(s, L)); });
    return out;
  };

  // =====================================================================
  // Draft (this browser only, 30 days)
  // =====================================================================
  P.saveDraft = function (now) {
    var self = this;
    if (this.ui.loading) return;
    clearTimeout(this._dt);
    // a letter opened from the cart is never saved as a draft; the letters still being written are
    var go = function () {
      var st = self.state, editing = st.sets.some(function (s) { return s.editKey; }), sets = st.sets.filter(function (s) { return !s.editKey; });
      if (editing && !sets.length) return;
      var save = editing ? Object.assign({}, st, { sets: sets, cur: Math.min(Math.max(0, st.cur - (st.sets.length - sets.length)), sets.length - 1) }) : st;
      try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ at: Date.now(), state: save })); } catch (e) {}
    };
    if (now) go(); else this._dt = setTimeout(go, 400);
  };
  // Arriving from the homepage preview (?santa_from=home&santa_name=…): carry the name, magical address,
  // language, handwriting and letter over, open the letter step, then tidy the address bar
  P.fromTeaser = function () {
    var q; try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (!q.get('santa_from')) return;
    var st = this.state, s = st.sets[st.cur] || st.sets[0];
    var lang = q.get('santa_lang'), name = q.get('santa_name'), addr = q.get('santa_addr'), L = q.get('santa_letter'), font = q.get('santa_font');
    if (['english', 'french', 'german'].indexOf(lang) >= 0) st.language = lang;
    if (name) { s.early.names = name.slice(0, 45); s.nameLinked = true; }
    if (addr) s.env.addr = addr.slice(0, 90);
    if (L === 'early' || L === 'morning') st.active = L;
    if (font === 'script' || font === 'classic') st.font = font;
    st.step = 'letters';
    try {
      var u = new URL(window.location.href);
      ['santa_from', 'santa_lang', 'santa_name', 'santa_addr', 'santa_letter', 'santa_font'].forEach(function (k) { u.searchParams.delete(k); });
      window.history.replaceState(window.history.state, '', u.toString());
    } catch (e) {}
  };
  P.restoreDraft = function () {
    try {
      var d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (!d || !d.state || !d.at || Date.now() - d.at > DRAFT_DAYS * 864e5) return;
      var st = d.state;
      if (!st.sets || !st.sets.length) return;
      st.sets = st.sets.map(fixSet).filter(function (s) { return !s.editKey; });
      if (!st.sets.length) return;
      this.state = Object.assign(this.state, st);
      if (CFG.language) this.state.language = this.state.language || CFG.language;
      if (this.state.cur >= this.state.sets.length) this.state.cur = 0;
      if (ORDER.indexOf(this.state.step) < 0) this.state.step = 'language';
    } catch (e) {}
  };
  P.clearDraft = function () { try { localStorage.removeItem(DRAFT_KEY); } catch (e) {} };

  // =====================================================================
  // Basket
  // =====================================================================
  P.loadBasket = function () {
    var self = this;
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; }).then(function (c) {
      var editing = self.state.sets.map(function (s) { return s.editKey; }).filter(Boolean);
      self.ui.basket = ((c && c.items) || []).filter(function (i) { return i.properties && i.properties._dml_state && editing.indexOf(i.key) < 0; }).map(function (i) {
        var st = {};
        try { st = JSON.parse(i.properties._dml_state) || {}; } catch (e) {}
        var title = i.product_title, meta = '', lang = st.language;
        if (st.product === 'santa' && st.set) {
          title = (UI[lang] || UI.english).titles[st.set.choice] || i.variant_title;
          meta = trim(st.set.early && st.set.early.names || st.set.morning && st.set.morning.names);
        }
        var image = i.image;
        if (st.data) {
          var ev = st.data.envelope || {}, envs = CFG.tfEnvelopes || {}, col = envs[ev.colour] || envs.sapphire;
          title = trim(ev.name) || i.product_title;
          var child = trim(st.data.letter && st.data.letter.childName);
          meta = [child && child !== title ? child : '', i.product_title].filter(Boolean).join(' · ');
          if (col) image = col[lang] || col.english || image;
        }
        var url = i.url + (i.url.indexOf('?') >= 0 ? '&' : '?') + 'dml_edit_key=' + encodeURIComponent(i.key) + '#builder';
        var n = (i.quantity || 1) * (st.product === 'santa' && st.set && st.set.choice === 'pair' ? 2 : 1);
        return { key: i.key, title: title, meta: meta, price: i.original_line_price != null ? i.original_line_price : i.final_line_price, letters: n, url: url, image: image, santa: st.product === 'santa' };
      });
      if (self.state.step === 'review') self.render();
    }).catch(function () {});
  };
  P.loadGift = function () {
    var self = this;
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.ok ? r.json() : null; }).then(function (c) {
      var a = (c && c.attributes) || {};
      if (a.Gift) { self.state.gift.on = true; self.state.gift.note = a['Gift note'] || ''; if (self.state.step === 'review') self.render(); }
    }).catch(function () {});
  };
  P.loadEdit = function (key) {
    var self = this;
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      var items = (c && c.items) || [], it = items.filter(function (i) { return i.key === key; })[0];
      var st = null;
      if (it) { try { st = JSON.parse(it.properties._dml_state); } catch (e) {} }
      if (st && st.product === 'santa' && st.set) {
        var s = fixSet(st.set);
        s.editKey = key;
        // the letters still being written stay in the review, next to the one opened from the cart
        self.restoreDraft();
        var keep = self.state.step !== 'language' || self.state.sets.some(function (x) { return self.picked(x).some(function (L) { return trim(self.val(x, L, 'names')); }); });
        self.state.sets = [s].concat(keep ? self.state.sets.filter(function (x) { return !x.editKey; }) : []); self.state.cur = 0;
        self.state.language = st.language || self.state.language;
        self.state.font = st.font || self.state.font;
        self.state.step = 'letters'; // "Edit letter" from the basket opens on the letter itself
        self.state.active = self.picked(s)[0];
      } else {
        try { var u = new URL(window.location.href); u.searchParams.delete('dml_edit_key'); window.history.replaceState(window.history.state, '', u.toString()); } catch (e) {}
        self.restoreDraft();
      }
      self.ui.loading = false;
      self.render();
      self.loadBasket();
    }).catch(function () { self.ui.loading = false; self.render(); });
  };

  // =====================================================================
  // Rendering
  // =====================================================================
  var PEN = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16.5 3.5l4 4L8 20H4v-4z"></path><path d="M14 6l4 4"></path></svg>';
  var EYE = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M1.5 10S4.5 4.5 10 4.5 18.5 10 18.5 10 15.5 15.5 10 15.5 1.5 10 1.5 10z"></path><circle cx="10" cy="10" r="2.6"></circle></svg>';
  var CROSS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.2" fill="none"></path></svg>';

  // The printed artwork (Shopify Files) behind the preview, in the letter's language
  P.skeletons = function () {
    var sk = CFG.skeletons || {}, L = this.L();
    if (this._skL === L) return;
    this._skL = L;
    if (sk.letter && sk.letter[L]) this.style.setProperty('--sk-letter', 'url("' + sk.letter[L] + '")');
    if (sk.envelope && sk.envelope[L]) this.style.setProperty('--sk-env', 'url("' + sk.envelope[L] + '")');
  };
  P.render = function () {
    var st = this.state, t = this.t(), idx = ORDER.indexOf(st.step), self = this;
    this.skeletons();
    if (this.ui.loading) { this.shell.innerHTML = '<div class="panel"><p class="sub">' + esc(t.loading) + '</p></div>'; return; }
    var stepper = '<nav class="stepper" aria-label="Steps">' + ORDER.map(function (k, i) {
      return '<button type="button" class="step' + (i === idx ? ' on' : (i < idx ? ' done' : '')) + '" data-act="go" data-step="' + k + '"' + (i === idx ? ' aria-current="step"' : '') + '><span class="dot">' + (i + 1) + '</span><span class="slabel">' + esc(t.steps[i]) + '</span></button>';
    }).join('') + '</nav>';
    var body = st.step === 'language' ? this.languageHTML() : st.step === 'letters' ? this.lettersHTML() : st.step === 'envelope' ? this.envelopeHTML() : this.reviewHTML();
    this.shell.innerHTML = stepper + '<main id="main" data-step="' + (idx + 1) + '">' + body + '</main>' + this.readerHTML() + this.proofHTML();
    this.grow();
    this.measure();
    this.saveDraft();
  };

  // soft refresh while typing: never touches the field being typed in
  P.soft = function () {
    var r = this.root, self = this, st = this.state, s = this.set();
    var pv = r.getElementById('pv');
    if (pv) pv.innerHTML = st.step === 'envelope' ? this.envPreviewsHTML() : st.step === 'letters' ? this.letterPreviewHTML() : this.pairPreviewHTML();
    var tabs = r.getElementById('ltabs');
    if (tabs) tabs.innerHTML = this.ltabsInner();
    var gs = r.getElementById('gender-slot');
    if (gs) gs.innerHTML = this.genderHTML();
    r.querySelectorAll('[data-lr]').forEach(function (el) {
      var p = el.getAttribute('data-lr').split('|');
      var item = p[0] === 'env' ? self.envItem(s, +p[1], p[2]) : self.fieldItem(s, p[1], p[2]);
      el.innerHTML = self.lblRowInner(item);
    });
    this.measure();
    this.saveDraft();
  };

  P.grow = function () {
    this.root.querySelectorAll('textarea.grow').forEach(function (el) { el.style.height = 'auto'; el.style.height = (el.scrollHeight + 2) + 'px'; });
  };

  // Sticky preview: when the letter is taller than the screen, let it scroll until its foot shows, then hold
  P.stick = function () {
    var el = this.root.querySelector('.preview-sticky-inner');
    if (!el) return;
    if (window.innerWidth < 981) { el.style.top = ''; return; }
    var head = parseFloat(getComputedStyle(this).getPropertyValue('--header-group-height')) || 100;
    var maxTop = head + 20, minTop = window.innerHeight - el.offsetHeight - 16, top = Math.min(maxTop, minTop);
    // Taller than the screen: follow the detail being typed, so its line in the letter stays in view
    var k = this.ui.focusK, pv = k && minTop < maxTop && el.querySelector('#pv [data-k="' + k + '"]');
    if (pv) {
      var y = pv.getBoundingClientRect().top - el.getBoundingClientRect().top;
      top = Math.max(minTop, Math.min(maxTop, Math.round(window.innerHeight * 0.4 - y)));
    }
    el.style.top = top + 'px';
  };
  // How many words fill the rest of this letter's page, from the real fonts and sizes on screen
  P.pageWords = function (s, L) {
    try {
      var wrap = this.root.getElementById('measure-wrap'), body = this.root.getElementById('measure-body');
      if (!wrap || !body || !wrap.clientHeight || this.activeLetter() !== L) return 0;
      var oldFit = body.style.getPropertyValue('--fit');
      body.style.setProperty('--fit', 1); // the target is counted at the specified sizes
      var cs = getComputedStyle(body), lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.25;
      var ctx = document.createElement('canvas').getContext('2d');
      ctx.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
      var sample = 'The first real snow came to the North Pole last night, and this morning the elves dragged the sleigh out of the barn.';
      var cpl = wrap.clientWidth / (ctx.measureText(sample).width / sample.length) * 0.92;
      var lines = Math.floor(wrap.clientHeight / lh);
      var used = 2 + 5 + 5; // greeting and its gap, closing lines and their gap, gaps between about six paragraphs
      var ps = trim(s[L].ps);
      if (ps) used += 2 + Math.ceil((ps.length + 6) / cpl);
      if (L === 'early' && s.slip) {
        var sb = this.root.querySelector('#measure-body .slipbox');
        used += 1 + (sb ? Math.ceil(sb.offsetHeight / lh) : 5);
      }
      body.style.setProperty('--fit', oldFit || 1);
      var perWord = { english: 5.9, french: 6.3, german: 7 }[this.L()] || 6;
      var w = Math.round(Math.max(0, lines - used) * cpl / perWord);
      return w >= 120 ? Math.min(w, 450) : 0;
    } catch (e) { return 0; }
  };
  // "One last read": each letter is fitted to its page the same way as on the letter step
  // (the writing grows or shrinks with the page, so it matches the letter step and the print)
  P.fitProof = function () {
    var bodies = this.root.querySelectorAll('.pf .letter-body');
    Array.prototype.forEach.call(bodies, function (body) {
      var wrap = body.parentNode;
      if (!wrap || !wrap.clientHeight) return;
      var lo = 0.7, hi = 1.3, fit;
      var fits = function (f) { body.style.setProperty('--fit', f); return body.scrollHeight <= wrap.clientHeight + 1; };
      if (fits(hi)) fit = hi;
      else if (!fits(lo)) fit = lo;
      else {
        var a = lo, b = hi;
        for (var n = 0; n < 9; n++) { var mid = (a + b) / 2; if (fits(mid)) a = mid; else b = mid; }
        fit = Math.floor(a * 1000) / 1000;
      }
      body.style.setProperty('--fit', fit);
    });
  };
  P.measure = function () {
    var self = this;
    requestAnimationFrame(function () {
      self.stick();
      if (self.ui.proof) self.fitProof();
      var wrap = self.root.getElementById('measure-wrap'), body = self.root.getElementById('measure-body');
      if (!wrap || !body || !wrap.clientHeight) return;
      var key = self.state.cur + self.activeLetter(), s = self.set(), L = self.activeLetter();
      // Fill the page: the writing grows until the text reaches the foot of the page, and shrinks when it
      // would run past the border (as on the Tooth Fairy letters). Own words: past the smallest size, the page is too long.
      var own = s.mode[L] === 'own', lo = own ? 0.72 : 0.7, hi = 1.3;
      var fits = function (f) { body.style.setProperty('--fit', f); return body.scrollHeight <= wrap.clientHeight + 1; };
      var fit;
      if (fits(hi)) fit = hi;
      else if (!fits(lo)) fit = lo;
      else {
        var a = lo, b = hi;
        for (var n = 0; n < 9; n++) { var mid = (a + b) / 2; if (fits(mid)) a = mid; else b = mid; }
        fit = Math.floor(a * 1000) / 1000;
      }
      // how full the page is at the smallest size: drives the meter and the length check
      body.style.setProperty('--fit', lo);
      var ratio = body.scrollHeight / wrap.clientHeight;
      body.style.setProperty('--fit', fit);
      self.ui.fits = self.ui.fits || {};
      self.ui.fits[key] = fit;
      // keep the full-size reader in step with the measured page
      var rb = self.ui.reader === 'letter' && self.root.querySelector('.reader .letter-body');
      if (rb) rb.style.setProperty('--fit', fit);
      var old = self.ui.fills[key] || 0;
      self.ui.fills[key] = ratio;
      var m = self.root.getElementById('meter');
      if (m) m.innerHTML = self.meterInner();
      if ((old > 1.001) !== (ratio > 1.001)) {
        var nb = self.root.getElementById('next-btn');
        if (nb) nb.disabled = self.blockedByLength();
      }
    });
  };
  P.blockedByLength = function () {
    var s = this.set(), L = this.activeLetter();
    return s.mode[L] === 'own' && (this.ui.fills[this.state.cur + L] || 0) > 1.001;
  };
  P.meterInner = function () {
    var s = this.set(), L = this.activeLetter(), t = this.t();
    if (s.mode[L] !== 'own') return '';
    var pct = Math.round((this.ui.fills[this.state.cur + L] || 0) * 100);
    return '<div class="meter"><span>' + esc(t.meter(pct)) + '</span><div class="bar ' + (pct > 100 ? 'over' : pct > 90 ? 'near' : '') + '"><span class="fill" style="width:' + Math.min(pct, 100) + '%"></span></div></div>' +
      (pct > 100 ? '<p class="warn">' + esc(t.too_long) + '</p>' : '');
  };

  P.pvCap = function (kind) {
    var t = this.t();
    return '<div class="pv-cap"><span class="pv-live">' + esc(t.live) + '</span><span class="pv-arrive">' + esc(t.arrive_door) + '</span>' +
      (kind ? '<button type="button" class="pv-read" data-act="reader" data-kind="' + kind + '">' + EYE + esc(kind === 'env' ? t.see_env : t.read) + '</button>' : '') + '</div>';
  };
  P.letterFrame = function (s, L, hl, measure) {
    return '<div class="frame letter-frame"><div class="letter-body-wrap"' + (measure ? ' id="measure-wrap"' : '') + '><div class="letter-body ' + this.state.font + (L === 'morning' ? ' m2' : ' m1') + '"' + (measure ? ' id="measure-body"' : '') + ' style="--fit:' + ((this.ui.fits || {})[this.state.sets.indexOf(s) + L] || 1) + '">' + this.letterHTML(s, L, hl) + '</div></div></div>';
  };

  // ---------- step 1 ----------
  P.pairPreviewHTML = function () {
    var s = this.set(), L = this.picked(s)[0];
    return '<div class="pair"><div class="mount env-mount">' + this.envHTML(this.envOf(s, L)) + '</div><div class="mount ltr-mount">' + this.letterFrame(s, L, true) + '</div></div>';
  };
  P.languageHTML = function () {
    var t = this.t(), st = this.state;
    var tiles = LANGS.map(function (l) {
      return '<button type="button" class="tile' + (st.language === l[0] ? ' on' : '') + '" data-act="lang" data-v="' + l[0] + '" aria-pressed="' + (st.language === l[0]) + '"><span class="t">' + l[1] + '</span><span class="d">' + esc(l[2]) + '</span></button>';
    }).join('');
    return '<div class="builder-grid"><div class="form-col"><div class="panel"><h2>' + esc(t.lang_title) + '</h2><p class="sub">' + esc(t.lang_sub) + '</p><div class="tiles">' + tiles + '</div>' +
      '<div class="actions"><span></span><button type="button" class="btn btn-invite" data-act="go" data-step="letters">' + esc(t.to_letter) + '</button></div></div></div>' +
      '<div class="preview-col"><div class="preview-sticky-inner"><div id="pv">' + this.pairPreviewHTML() + '</div>' + this.pvCap('letter') + '</div></div></div>';
  };

  // ---------- step 2 ----------
  P.fieldItem = function (s, L, k) {
    var t = this.t(), key = k;
    if (L === 'morning' && k === 'way') key = 'way2';
    if (L === 'morning' && k === 'treat') key = 'treat2';
    var f = this.fl(key), v = this.val(s, L, k), max = this.lim(k);
    var linkable = L === 'morning' && s.choice === 'pair' && s.links[k] !== undefined;
    var help = f[1];
    if (k === 'growth' && s.choice === 'pair') help += this.fl('growth_pair');
    return {
      L: L, k: k, id: 'f-' + this.state.cur + '-' + L + '-' + k, label: f[0], help: help, ph: f[2], value: v, max: max,
      linkTag: linkable && s.links[k] && !!trim(v), unlinked: linkable && !s.links[k], linkText: t.from_1, relinkText: t.relink_1,
      count: v.length, showCount: k !== 'own' && v.length > max * 0.8,
      ai: AI_FIELDS.indexOf(k) >= 0, area: ['milestone', 'habit', 'past', 'growth', 'ps', 'own'].indexOf(k) >= 0, big: k === 'own'
    };
  };
  P.lblRowInner = function (it) {
    return '<label for="' + it.id + '">' + esc(it.label) + '</label>' +
      (it.linkTag ? '<span class="tag">' + esc(it.linkText) + '</span>' : '') +
      (it.unlinked ? '<button type="button" class="relink" data-act="relink" data-k="' + it.k + '"' + (it.env ? ' data-env="' + it.env + '"' : '') + '>' + esc(it.relinkText) + '</button>' : '') +
      (it.showCount ? '<span class="counter">' + it.count + ' / ' + it.max + '</span>' : '');
  };
  P.museHTML = function (it, cfgKey) {
    var t = this.t(), key = it.id, open = !!this.ui.muse[key], busy = this.ui.gen === key, m = (MUSE[this.L()] || MUSE.english)[cfgKey];
    var btn = busy ? t.generating : cfgKey === 'own' ? t.gen_letter : cfgKey === 'address' ? t.gen_addr : t.gen;
    return '<div class="helper"><button type="button" class="help-toggle" aria-expanded="' + open + '" data-act="muse" data-key="' + key + '">' + PEN + esc(it.k === 'own' ? t.help_write : t.inspire) + '</button>' +
      (open ? '<div class="muse"><label for="' + key + '-muse">' + esc(m[0]) + '</label><div class="row"><input type="text" id="' + key + '-muse" data-prompt="' + key + '" maxlength="150" autocomplete="off" value="' + esc(this.ui.prompts[key] || '') + '" placeholder="' + esc(m[1]) + '">' +
        '<button type="button" class="muse-go' + (busy ? ' loading' : '') + '" data-act="gen" data-key="' + key + '" data-cfg="' + cfgKey + '" data-l="' + (it.L || '') + '" data-k="' + it.k + '"' + (it.env ? ' data-env="' + it.env + '"' : '') + (busy ? ' disabled' : '') + '>' + esc(btn) + '</button></div>' +
        (this.ui.aiErr[key] ? '<p class="ai-err">' + esc(t.ai_err) + '</p>' : '') +
        '<small>' + esc(t.ai_note + (cfgKey === 'own' || cfgKey === 'address' ? '' : t.ai_short)) + '</small></div>' : '') + '</div>';
  };
  P.fieldHTML = function (it) {
    var ctl = it.area
      ? '<textarea id="' + it.id + '" class="' + (it.big ? 'big' : 'grow') + '" rows="1" data-f="' + it.L + '|' + it.k + '" maxlength="' + it.max + '" placeholder="' + esc(it.ph) + '">' + esc(it.value) + '</textarea>'
      : '<input type="text" id="' + it.id + '" data-f="' + it.L + '|' + it.k + '" maxlength="' + it.max + '" value="' + esc(it.value) + '" placeholder="' + esc(it.ph) + '" autocomplete="off">';
    return '<div class="field"><div class="lbl-row" data-lr="f|' + it.L + '|' + it.k + '">' + this.lblRowInner(it) + '</div>' + ctl +
      (it.help ? '<span class="help">' + esc(it.help) + '</span>' : '') + (it.ai ? this.museHTML(it, it.k) : '') + '</div>';
  };
  // One child / two or more, one grown-up / two or more: read from the text, the parent can change it
  P.countHTML = function (kind) {
    var s = this.set(), L = this.activeLetter(), t = this.t();
    var many = kind === 'kids' ? this.isSib(s, L) : this.isMany(s, L), auto = !s.count || s.count[kind] === 'auto';
    var opts = kind === 'kids' ? t.kids_o : t.grown_o;
    return '<div class="field count-row"><div class="lbl-row"><span class="lbl">' + esc(kind === 'kids' ? t.kids_l : t.grown_l) + '</span>' + (auto ? '<span class="tag">' + esc(t.count_auto) + '</span>' : '') + '</div><div class="seg" role="group">' + opts.map(function (o) {
      var on = (o[0] === 'many') === many;
      return '<button type="button" class="seg-btn' + (on ? ' on' : '') + '" data-act="count" data-k="' + kind + '" data-v="' + o[0] + '" aria-pressed="' + on + '">' + esc(o[1]) + '</button>';
    }).join('') + '</div></div>';
  };
  P.genderHTML = function () {
    if (this.L() === 'english') return '';
    var s = this.set(), L = this.activeLetter(), t = this.t(), sib = this.isSib(s, L), opts = sib ? t.g_many : t.g_one;
    if (!opts.length) return '';
    return '<div class="field"><div class="lbl-row"><span class="lbl">' + esc(t.gender) + '</span></div><div class="seg" role="group">' + opts.map(function (o) {
      return '<button type="button" class="seg-btn' + (s.gender === o[0] ? ' on' : '') + '" data-act="gender" data-v="' + o[0] + '" aria-pressed="' + (s.gender === o[0]) + '">' + esc(o[1]) + '</button>';
    }).join('') + '</div></div>';
  };
  P.ltabsInner = function () {
    var self = this, s = this.set(), L = this.activeLetter(), t = this.t();
    return ['early', 'morning'].map(function (k) {
      var n = self.missingFor(s, k).length;
      return '<button type="button" class="ltab' + (L === k ? ' on' : (n ? '' : ' ready')) + '" data-act="ltab" data-v="' + k + '" aria-pressed="' + (L === k) + '"><span class="lk">' + esc(t.kicker[k]) + '</span><span class="lt">' + esc(t.short[k]) + '</span><span class="ls">' + esc(L === k ? t.writing_now : (n ? t.to_add(n) : t.all_in)) + '</span></button>';
    }).join('');
  };
  P.letterPreviewHTML = function () {
    var s = this.set(), L = this.activeLetter();
    return '<div class="solo"><div class="mount ltr-mount" data-act="reader" data-kind="letter" title="' + esc(this.t().read) + '">' + this.letterFrame(s, L, true, true) + '</div></div>';
  };
  P.lettersHTML = function () {
    var self = this, t = this.t(), s = this.set(), L = this.activeLetter(), pair = s.choice === 'pair', own = s.mode[L] === 'own';
    var tiles = ['early', 'morning', 'pair'].map(function (k) {
      var save = k === 'pair' ? '<span class="sv">' + esc(t.save(self.money(self.price('early') + self.price('morning')))) + esc(self.money(self.price('early') + self.price('morning') - self.price('pair'))) + '</span>' : '';
      return '<button type="button" class="tile' + (s.choice === k ? ' on' : '') + '" role="radio" aria-checked="' + (s.choice === k) + '" data-act="choice" data-v="' + k + '"><span class="tw"><span class="k">' + esc(t.kicker[k]) + '</span><span class="t">' + esc(t.short[k]) + '</span></span><span class="pr"><span class="p">' + esc(self.money(self.price(k))) + '</span>' + save + '</span></button>';
    }).join('');
    var hand = '<div class="field"><div class="lbl-row"><span class="lbl">' + esc(t.hand) + '</span></div><div class="seg" role="group">' +
      [['script', t.hand_script], ['classic', t.hand_classic]].map(function (o) {
        return '<button type="button" class="seg-btn' + (self.state.font === o[0] ? ' on' : '') + '" data-act="font" data-v="' + o[0] + '" aria-pressed="' + (self.state.font === o[0]) + '"><span class="fs ' + o[0] + '">' + esc(o[1]) + '</span></button>';
      }).join('') + '</div>' + (pair ? '<p class="hand-both">' + esc(t.hand_both) + '</p>' : '') + '</div>';
    var tabs = pair ? '<div class="ltabs" id="ltabs" role="group">' + this.ltabsInner() + '</div>' + (L === 'morning' ? '<p class="hint">' + esc(t.carry) + '</p>' : '') : '';
    var words = own
      ? '<div class="words-box own"><p class="wb-t">' + esc(t.own_t) + '</p><p class="wb-a"><button type="button" class="btn-link" data-act="santa-words">' + esc(t.own_back) + '</button><span class="sep">·</span><button type="button" class="btn-link" data-act="blank">' + esc(t.words_blank) + '</button></p></div>'
      : '<div class="words-box"><p class="wb-t">' + esc(t.words_t) + '</p><p class="wb-d">' + esc(t.words_d) + '</p><p class="wb-a"><button type="button" class="btn-link" data-act="edit-words">' + esc(t.words_edit) + '</button><span class="sep">·</span><button type="button" class="btn-link" data-act="blank">' + esc(t.words_blank) + '</button></p></div>';
    var f = function (k) { return self.fieldHTML(self.fieldItem(s, L, k)); };
    var elves = function () {
      return '<div class="elf-box"><p class="slip-h">' + esc(t.elves_t) + '</p><p class="elf-note">' + esc(t.elves_d) + '</p>' + ['location', 'way', 'hazard', 'treat', 'habit'].map(function (k) { return self.fieldHTML(self.fieldItem(s, 'early', k)); }).join('') + '</div>';
    };
    var fields = f('names') + '<div id="gender-slot">' + this.genderHTML() + '</div>';
    var grown = function () { return self.usesGrown(L) ? f('grown') : ''; };
    if (own) {
      fields += f('own');
      if (L === 'early') {
        fields += '<div class="field"><label class="checkbox-row"><input type="checkbox" data-act="keep-elves"' + (s.slip ? ' checked' : '') + '><span>' + esc(t.keep_elves) + '</span></label></div>';
        if (s.slip) fields += elves();
      }
    } else if (L === 'early') {
      fields += f('outing') + f('milestone') + elves() + grown();
    } else {
      fields += f('way') + f('treat') + f('past') + f('growth') + grown() + f('place');
    }
    fields += f('ps');
    var nextLabel = pair && L === 'early' ? t.to_2 : t.to_env;
    return '<div class="builder-grid"><div class="form-col"><div class="panel">' +
      '<h2>' + esc(t.letter_title) + '</h2><p class="sub">' + esc(this.state.sets.length > 1 ? t.letter_of(this.state.cur + 1, this.state.sets.length) + ' · ' + t.letter_sub : t.letter_sub) + '</p>' +
      // the handwriting first (it changes how the letter reads), then the letters, then "Want different words?"
      hand + '<div class="tiles letters" role="radiogroup">' + tiles + '</div><p class="note">' + esc(t.desc[s.choice]) + '</p>' +
      tabs + words + fields +
      '<div class="actions"><button type="button" class="btn btn-back" data-act="prev">' + esc(t.back) + '</button><button type="button" class="btn btn-invite" id="next-btn" data-act="next"' + (this.blockedByLength() ? ' disabled' : '') + '>' + esc(nextLabel) + '</button></div>' +
      '</div></div><div class="preview-col"><div class="preview-sticky-inner"><div id="pv">' + this.letterPreviewHTML() + '</div>' + this.pvCap('letter') + '<div id="meter">' + this.meterInner() + '</div></div></div></div>';
  };

  // ---------- step 3 ----------
  P.envItem = function (s, which, k) {
    var t = this.t(), key = k === 'name' ? 'env_name' : k === 'addr' ? 'env_addr' : 'env_city', f = this.fl(key);
    var v = which === 1 ? (k === 'name' && s.nameLinked ? this.childNames(s) : s.env[k]) : (s.envLinks[k] ? this.envVal(s, 1, k) : s.env2[k]);
    var it = { env: which, k: k, id: 'e-' + this.state.cur + '-' + which + '-' + k, label: f[0], help: which === 1 ? f[1] : '', ph: f[2], value: v || '', max: this.lim(k), ai: k === 'addr' };
    if (which === 1 && k === 'name') { it.linkTag = s.nameLinked && !!trim(v); it.unlinked = !s.nameLinked; it.linkText = t.from_letter; it.relinkText = t.relink_name; }
    if (which === 2) { it.linkTag = s.envLinks[k] && !!trim(v); it.unlinked = !s.envLinks[k]; it.linkText = t.as_first; it.relinkText = t.relink_first; }
    return it;
  };
  P.envFieldHTML = function (it) {
    return '<div class="field"><div class="lbl-row" data-lr="env|' + it.env + '|' + it.k + '">' + this.lblRowInner(it) + '</div>' +
      '<input type="text" id="' + it.id + '" data-e="' + it.env + '|' + it.k + '" maxlength="' + it.max + '" value="' + esc(it.value) + '" placeholder="' + esc(it.ph) + '" autocomplete="off">' +
      (it.help ? '<span class="help">' + esc(it.help) + '</span>' : '') + (it.ai ? this.museHTML(it, 'address') : '') + '</div>';
  };
  P.envPreviewsHTML = function () {
    var self = this, s = this.set(), t = this.t(), p = this.picked(s);
    return '<div class="envs">' + p.map(function (L, i) {
      return '<div><div class="mount env-mount' + (i ? ' second' : '') + '">' + self.envHTML(self.envOf(s, L)) + '</div><p class="env-cap">' + esc((p.length > 1 ? t.kicker[L] + ' · ' : '') + t.env_head[L]) + '</p></div>';
    }).join('') + '</div>';
  };
  P.envelopeHTML = function () {
    var self = this, t = this.t(), s = this.set(), pair = s.choice === 'pair', boxed = pair && s.envDiffer;
    var group = function (which, L) {
      var inner = ['name', 'addr', 'city'].map(function (k) { return self.envFieldHTML(self.envItem(s, which, k)); }).join('');
      return boxed ? '<div class="env-group boxed"><p class="eg-head"><span class="eg-k">' + esc(t.kicker[L]) + '</span>' + esc(t.env_head[L]) + '</p>' + inner + '</div>' : '<div class="env-group">' + inner + '</div>';
    };
    var groups = group(1, pair ? 'early' : s.choice) + (boxed ? group(2, 'morning') : '');
    var differ = pair ? '<div class="words-box"><p class="wb-t">' + esc(s.envDiffer ? t.diff_t : t.same_t) + '</p><p class="wb-a"><button type="button" class="btn-link" data-act="differ">' + esc(s.envDiffer ? t.same_l : t.diff_l) + '</button></p></div>' : '';
    return '<div class="builder-grid"><div class="form-col"><div class="panel"><h2>' + esc(t.env_title) + '</h2><p class="sub">' + esc(t.env_sub) + '</p>' + groups + differ +
      '<div class="actions"><button type="button" class="btn btn-back" data-act="prev">' + esc(t.back) + '</button><button type="button" class="btn btn-invite" data-act="next">' + esc(t.to_review) + '</button></div></div></div>' +
      '<div class="preview-col"><div class="preview-sticky-inner"><div id="pv">' + this.envPreviewsHTML() + '</div>' + this.pvCap('env') + '</div></div></div>';
  };

  // ---------- step 4 ----------
  P.setCardHTML = function (s, si) {
    var self = this, t = this.t(), pair = s.choice === 'pair', names = this.childNames(s) || t.unnamed, armed = this.ui.armed;
    var rows = this.picked(s).map(function (L) {
      var miss = self.missingFor(s, L).concat(self.missingEnv(s, L)), ak = si + '-' + L;
      return '<div class="lrow' + (miss.length ? ' incomplete' : '') + '">' + self.envHTML(self.envOf(s, L), 'rv-thumb') +
        '<div class="lr-info"><p class="lr-k">' + esc(t.kicker[L]) + '</p><p class="lr-t">' + esc(t.titles[L]) + '</p>' +
        '<p class="lc-meta">' + esc(t.for_x(names) + ' · ' + (s.mode[L] === 'own' ? t.own_words : t.santa_words) + ' · ' + (self.state.font === 'script' ? t.hand_script : t.hand_classic)) + '</p>' +
        '<p class="lr-acts"><button type="button" class="btn-link" data-act="edit-letter" data-set="' + si + '" data-v="' + L + '">' + esc(t.edit_letter) + '</button><button type="button" class="btn-link" data-act="edit-env" data-set="' + si + '">' + esc(t.edit_env) + '</button></p></div>' +
        '<div class="lr-side">' + (pair ? '' : '<p class="lc-price">' + esc(self.money(self.price(L))) + '</p>') +
        '<button type="button" class="lr-remove' + (armed === ak ? ' armed' : '') + '" data-act="rm-letter" data-set="' + si + '" data-v="' + L + '">' + CROSS + esc(armed === ak ? (pair ? t.remove_this : t.remove_yes) : t.remove) + '</button></div>' +
        (miss.length ? '<div class="lc-missing"><p class="lc-missing-title">' + esc(t.missing_t) + '</p><div class="lc-missing-list">' + miss.map(function (m) {
          return '<button type="button" class="lc-missing-link" data-act="goto" data-set="' + si + '" data-v="' + m.L + '" data-step="' + m.step + '" data-field="' + esc(m.field || '') + '">' + esc(m.label) + '</button>';
        }).join('') + '</div></div>' : '') + '</div>';
    }).join('');
    var head = pair ? '<div class="set-head"><div><p class="lc-title">' + esc(t.titles.pair) + '</p><p class="lc-meta">' + esc(t.pair_meta(names)) + '</p></div><div class="set-price"><p class="lc-price">' + esc(this.money(this.price('pair'))) + '</p><p class="set-save">' + esc(t.save(this.money(this.price('early') + this.price('morning')))) + esc(this.money(this.price('early') + this.price('morning') - this.price('pair'))) + '</p></div></div>' : '';
    var foot = pair ? '<div class="set-foot"><button type="button" class="lr-remove' + (armed === si + '-set' ? ' armed' : '') + '" data-act="rm-set" data-set="' + si + '">' + CROSS + esc(armed === si + '-set' ? t.remove_both_yes : t.remove_both) + '</button></div>' : '';
    var other = s.choice === 'early' ? 'morning' : 'early';
    var ups = pair ? '' : '<div class="upsell"><p>' + esc(t.upsell(t.titles[other], this.money(this.price('pair') - this.price(s.choice)))) + '</p><button type="button" class="add-sib" data-act="upsell" data-set="' + si + '" data-v="' + other + '">' + esc(t.add_for(this.money(this.price('pair') - this.price(s.choice)))) + '</button></div>';
    return '<div class="set-card">' + head + rows + foot + '</div>' + ups;
  };
  P.reviewHTML = function () {
    var self = this, t = this.t(), st = this.state, sets = st.sets, b = this.ui.basket;
    var letters = 0, total = 0;
    sets.forEach(function (s) { letters += self.picked(s).length; total += self.price(s.choice); });
    var cards = sets.map(function (s, i) { return self.setCardHTML(s, i); }).join('<div class="set-gap"></div>');
    var basket = b.length ? '<section class="rv-group rv-basket"><p class="rv-head">' + esc(t.basket_t) + '</p>' + b.map(function (x) {
      return '<div class="letter-card bk">' + (x.image ? '<div class="bk-img" style="background-image:url(\'' + esc(x.image) + '\')"></div>' : '<div class="bk-img"></div>') +
        '<div><p class="lc-title">' + esc(x.title) + '</p><p class="lc-meta">' + esc(x.meta) + '</p></div><div class="rv-side"><p class="lc-price">' + esc(self.money(x.price)) + '</p><div class="lc-actions"><a class="btn-link" href="' + esc(x.url) + '">' + esc(t.edit) + '</a>' +
        '<button type="button" class="btn-link danger' + (self.ui.armed === 'bk-' + x.key ? ' armed' : '') + '" data-act="rm-basket" data-key="' + esc(x.key) + '">' + esc(self.ui.armed === 'bk-' + x.key ? t.remove_yes : t.remove) + '</button></div></div></div>';
    }).join('') + '</section>' : '';
    var bTotal = 0, bLetters = 0;
    b.forEach(function (x) { bTotal += x.price || 0; bLetters += x.letters || 1; });
    // Bundle discount (Shopify automatic discount "Bundle discount"): 15% off from 3 letters, the Pair counts as 2
    var allLetters = letters + bLetters, bundleOn = allLetters >= 3, bundle = bundleOn ? Math.round((total + bTotal) * 0.15) : 0;
    var bundleNote = '<p class="bundle-note' + (bundleOn ? ' on' : '') + '">' + esc(bundleOn ? t.bundle_on : (allLetters === 2 ? t.bundle_nudge : t.bundle_hint)) + '</p>';
    var rows = sets.map(function (s) { return '<div class="os-row"><span>' + esc(t.titles[s.choice] + (sets.length > 1 ? ' · ' + (self.childNames(s) || t.unnamed) : '')) + '</span><span class="v">' + esc(self.money(self.price(s.choice))) + '</span></div>'; }).join('') +
      (b.length ? '<div class="os-row"><span>' + esc(t.in_basket(b.length)) + '</span><span class="v">' + esc(this.money(bTotal)) + '</span></div>' : '') +
      (bundleOn ? '<div class="os-row os-bundle"><span>' + esc(t.bundle_row) + '</span><span class="v">−' + esc(this.money(bundle)) + '</span></div>' : '') +
      '<div class="os-row tot"><span>' + esc(b.length ? t.basket_total : t.total) + '</span><span class="v">' + esc(this.money(total + bTotal - bundle)) + '</span></div>';
    var arrive = sets.some(function (s) { return s.choice === 'pair'; }) ? t.arrive.pair : sets.some(function (s) { return s.choice === 'morning'; }) ? t.arrive.morning : '';
    var ob = this.orderByHTML();
    var editing = sets.some(function (s) { return s.editKey; });
    return '<div class="panel review"><h2>' + esc(t.review_title) + '</h2><p class="sub">' + esc(t.review_sub(sets.length, letters)) + '</p>' +
      '<section class="rv-group rv-new"><p class="rv-head">' + esc(letters > 1 ? t.your_letters : t.your_letter) + '</p>' + cards +
      '<button type="button" class="add-sib sib" data-act="sibling">' + esc(t.sibling) + '</button>' + bundleNote + '</section>' + basket +
      '<section class="rv-group rv-order"><p class="rv-head">' + esc(t.order_t) + '</p>' + (this.shipFrom() ? '<p class="rv-incl rv-ship" style="color:var(--rubric)">' + esc(this.shipFrom()) + '</p>' : '') + '<p class="rv-incl">' + esc(t.incl) + '</p>' + (arrive ? '<p class="rv-incl">' + esc(arrive) + '</p>' : '') +
      '<div class="giftbox"><label class="checkbox-row"><input type="checkbox" data-act="gift"' + (st.gift.on ? ' checked' : '') + '><span>' + esc(t.gift) + '</span></label>' +
      (st.gift.on ? '<div class="g-more"><p class="g-info">' + esc(t.gift_info) + '</p><div class="field"><div class="lbl-row"><label for="gift-note">' + esc(t.gift_note) + '</label></div><input type="text" id="gift-note" data-gift="1" maxlength="150" value="' + esc(st.gift.note) + '" placeholder="' + esc(t.gift_ph) + '"></div></div>' : '') + '</div>' +
      '<div class="order-summary">' + rows + '</div>' + ob + '<p class="disc">' + esc(t.disc) + '</p></section>' +
      (this.ui.err ? '<p class="warn">' + esc(this.ui.err) + '</p>' : '') +
      '<div class="actions"><button type="button" class="btn btn-back" data-act="prev">' + esc(t.back) + '</button><button type="button" class="btn btn-primary" data-act="proof">' + esc(editing ? t.update : (letters > 1 ? t.add_n : t.add_one)) + '</button></div></div>';
  };

  // First dispatch date of the season (section setting): shown on the review step until it passes
  P.shipFrom = function () {
    var v = CFG.shipFrom;
    if (!v || !this.t().ship_from) return '';
    var dt = new Date(v + 'T00:00:00');
    if (isNaN(dt) || dt <= new Date()) return '';
    var txt;
    try { txt = new Intl.DateTimeFormat(LOCALE[this.L()], { day: 'numeric', month: 'long' }).format(dt); } catch (e) { txt = v; }
    return this.t().ship_from(txt);
  };

  // Order-by date for the delivery country: the country Shopify has for the shopper (country selector /
  // location), which they can change here (e.g. a gift posted abroad).
  var OB_EU = 'AT BE BG HR CY CZ DK EE FI FR GR HU IE IT LV LT LU MC MT NL PL PT RO SK SI ES SE'.split(' ');
  var OB_SHIP = ['DE', 'CH', 'LI', 'US', 'AU', 'NZ'].concat(OB_EU);
  P.obKey = function (c) { return c === 'DE' ? 'de' : (c === 'CH' || c === 'LI') ? 'ch' : c === 'US' ? 'us' : OB_EU.indexOf(c) >= 0 ? 'eu' : 'other'; };
  P.orderByHTML = function () {
    var ob = CFG.orderBy || {}, d = ob.dates || {}, t = this.t(), loc = LOCALE[this.L()] || 'en-GB';
    var c = String(this.ui.obCountry || ob.country || 'DE').toUpperCase();
    var list = OB_SHIP.slice();
    if (list.indexOf(c) < 0) list.push(c);
    var v = d[this.obKey(c)] || d.other;
    if (!v) return '';
    var dt = new Date(v + 'T23:59:59');
    if (isNaN(dt) || dt < new Date()) return '';
    var txt;
    try { txt = new Intl.DateTimeFormat(loc, { day: 'numeric', month: 'long' }).format(dt); } catch (e) { txt = v; }
    var dn = null;
    try { dn = new Intl.DisplayNames([loc], { type: 'region' }); } catch (e) {}
    var opts = list.map(function (x) { var n = x; try { n = dn ? dn.of(x) : x; } catch (e) {} return [x, n]; })
      .sort(function (a, b) { return a[1].localeCompare(b[1], loc); });
    var sel = '<select class="ob-country" data-act="ob-country" aria-label="' + esc(t.ship_to) + '">' + opts.map(function (o) {
      return '<option value="' + o[0] + '"' + (o[0] === c ? ' selected' : '') + '>' + esc(o[1]) + '</option>';
    }).join('') + '</select>';
    return '<p class="order-by">' + esc(t.order_by(txt)) + ' <span class="ob-to">' + esc(t.ship_to) + ' ' + sel + '</span></p>';
  };

  // ---------- overlays ----------
  P.readerHTML = function () {
    var r = this.ui.reader;
    if (!r) return '';
    var s = this.set(), L = this.state.step === 'letters' ? this.activeLetter() : this.picked(s)[0];
    var inner = r === 'env' ? this.envHTML(this.envOf(s, L)) : this.letterFrame(s, L, false);
    var t = this.t(), zoom = r === 'env' ? '' : '<button type="button" class="reader-zoom" data-act="reader-zoom" aria-pressed="false"><span class="rz-in">＋ ' + esc(t.zoom_in) + '</span><span class="rz-out">− ' + esc(t.zoom_out) + '</span></button>';
    return '<div class="reader" role="dialog" aria-modal="true"><button type="button" class="reader-close" data-act="reader-close" aria-label="' + esc(t.close) + '">×</button>' + zoom + '<div class="reader-box ' + (r === 'env' ? 'is-env' : 'is-letter" data-act="reader-zoom') + '">' + inner + '</div></div>';
  };
  // Spelling and grammar check on the final read (worker /proofread, run on request)
  P.wordDiff = function (a, b) {
    var A = String(a).split(/(\s+)/), B = String(b).split(/(\s+)/), n = A.length, m = B.length, i, j;
    var L = []; for (i = 0; i <= n; i++) { L.push(new Array(m + 1).fill(0)); }
    for (i = n - 1; i >= 0; i--) for (j = m - 1; j >= 0; j--) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    var out = '', run = '', flush = function () { if (run) { out += /\S/.test(run) ? '<mark>' + esc(run) + '</mark>' : esc(run); run = ''; } };
    i = 0; j = 0;
    while (j < m) {
      if (i < n && A[i] === B[j]) { flush(); out += esc(B[j]); i++; j++; }
      else if (i < n && L[i + 1][j] >= L[i][j + 1]) { i++; }
      else { run += B[j]; j++; }
    }
    flush();
    return out;
  };
  P.proofreadHTML = function () {
    var t = this.t(), pr = this.ui.pr, self = this;
    if (!trim(CFG.worker)) return '';
    if (!pr) return '<div class="pr-box"><button type="button" class="btn btn-invite pr-run" data-act="pr-run">' + esc(t.pr_run) + '</button><p class="pr-note">' + esc(t.pr_note) + '</p></div>';
    if (pr.status === 'checking') return '<div class="pr-box"><p class="pr-status">' + esc(t.pr_checking) + '</p></div>';
    if (pr.status === 'error') return '<div class="pr-box"><p class="pr-status">' + esc(t.pr_error) + '</p></div>';
    if (!pr.fixes.length) return '<div class="pr-box"><p class="pr-status ok">' + esc(pr.found ? t.pr_done : t.pr_none) + '</p></div>';
    return '<div class="pr-box"><p class="pr-status">' + esc(t.pr_found(pr.fixes.length)) + '</p>' + pr.fixes.map(function (f, i) {
      return '<div class="pr-item"><p class="pr-where">' + esc(f.where) + '</p>' +
        '<p class="pr-line"><span class="pr-lab">' + esc(t.pr_yours) + '</span> ' + esc(f.orig) + '</p>' +
        (f.unclear ? '' : '<p class="pr-line sug"><span class="pr-lab">' + esc(t.pr_sugg) + '</span> ' + self.wordDiff(f.orig, f.text) + '</p>') +
        (f.note ? '<p class="pr-why">' + esc(f.note) + '</p>' : '') +
        (f.editing
          ? '<div class="pr-edit"><textarea class="pr-ta" rows="2" data-pr-text="' + i + '" maxlength="' + self.lim(f.id.split('|')[2]) + '">' + esc(f.draft != null ? f.draft : f.orig) + '</textarea>' +
            '<div class="pr-acts"><button type="button" class="btn btn-invite pr-use" data-act="pr-save" data-v="' + i + '">' + esc(t.pr_save) + '</button><button type="button" class="btn-link" data-act="pr-cancel" data-v="' + i + '">' + esc(t.pr_cancel) + '</button></div></div>'
          : '<div class="pr-acts">' + (f.unclear ? '<button type="button" class="btn btn-invite pr-use" data-act="pr-edit" data-v="' + i + '">' + esc(t.pr_edit) + '</button>' : '<button type="button" class="btn btn-invite pr-use" data-act="pr-use" data-v="' + i + '">' + esc(t.pr_use) + '</button><button type="button" class="btn-link" data-act="pr-edit" data-v="' + i + '">' + esc(t.pr_edit) + '</button>') + '<button type="button" class="btn-link" data-act="pr-keep" data-v="' + i + '">' + esc(t.pr_keep) + '</button></div>') + '</div>';
    }).join('') + '</div>';
  };
  P.proofHTML = function () {
    if (!this.ui.proof) return '';
    var self = this, t = this.t(), sets = this.state.sets, missingAll = 0;
    var items = sets.map(function (s, si) {
      var p = self.picked(s);
      return p.map(function (L, i) {
        var miss = self.missingFor(s, L).concat(self.missingEnv(s, L));
        missingAll += miss.length;
        var head = (sets.length > 1 ? (self.childNames(s) || t.unnamed) + ' · ' : '') + (p.length > 1 ? t.letter_of(i + 1, p.length) + ' · ' : '') + t.titles[L];
        return '<section class="pf-item"><p class="pf-ih"><span class="pf-n">' + esc(head) + '</span><button type="button" class="btn-link" data-act="edit-letter" data-set="' + si + '" data-v="' + L + '">' + esc(t.edit) + '</button></p>' +
          (miss.length ? '<div class="pf-miss"><p class="lc-missing-title">' + esc(t.pf_missing) + '</p><div class="lc-missing-list">' + miss.map(function (m) {
            return '<button type="button" class="lc-missing-link" data-act="goto" data-set="' + si + '" data-v="' + m.L + '" data-step="' + m.step + '" data-field="' + esc(m.field || '') + '">' + esc(m.label) + '</button>';
          }).join('') + '</div></div>' : '') +
          '<div class="pf-pair"><div><div class="mount">' + self.letterFrame(s, L, true) + '</div><p class="pf-cap">' + esc(t.pf_letter) + '</p></div><div><div class="mount">' + self.envHTML(self.envOf(s, L)) + '</div><p class="pf-cap">' + esc(t.pf_env) + '</p></div></div></section>';
      }).join('');
    }).join('');
    var editing = sets.some(function (s) { return s.editKey; }), letters = 0;
    // the parent's own words are highlighted in the popup (not in print), unless every letter is in their own words
    var anyFill = sets.some(function (s) { return self.picked(s).some(function (L) { return s.mode[L] !== 'own'; }); });
    sets.forEach(function (s) { letters += self.picked(s).length; });
    var can = !missingAll && this.ui.confirm && !this.ui.adding;
    return '<div class="reader pf-wrap" role="dialog" aria-modal="true" aria-labelledby="pf-title"><div class="pf"><button type="button" class="pf-close" data-act="proof-close" aria-label="' + esc(t.close) + '">×</button><header class="pf-head"><span class="pf-k">' + esc(t.pf_k) + '</span><h2 id="pf-title">' + esc(t.pf_t) + '</h2><p>' + esc(t.pf_d) + '</p>' + (anyFill ? '<p class="pf-hl">' + esc(t.pf_hl_a) + '<mark class="fill">' + esc(t.pf_hl_word) + '</mark>' + esc(t.pf_hl_b) + '</p>' : '') + '</header>' + this.proofreadHTML() + items +
      '<footer class="pf-foot"><label class="checkbox-row"><input type="checkbox" data-act="confirm"' + (this.ui.confirm ? ' checked' : '') + (missingAll ? ' disabled' : '') + '><span>' + esc(t.confirm) + '</span></label><p class="pf-hint">' + esc(t.confirm_hint) + '</p>' +
      (this.ui.err ? '<p class="warn">' + esc(this.ui.err) + '</p>' : '') +
      '<div class="actions"><button type="button" class="btn btn-back" data-act="proof-close">' + esc(t.keep_editing) + '</button><button type="button" class="btn btn-primary" data-act="add"' + (can ? '' : ' disabled') + '>' + esc(this.ui.adding ? t.adding : (editing ? t.update : (letters > 1 ? t.add_n : t.add_one))) + '</button></div></footer></div></div>';
  };

  // =====================================================================
  // Events
  // =====================================================================
  P.scrollToTop = function () {
    try {
      var y = this.getBoundingClientRect().top + window.pageYOffset - 110;
      if (window.pageYOffset > y) window.scrollTo({ top: y, behavior: 'smooth' });
    } catch (e) {}
  };
  P.go = function (step) {
    this.state.step = step;
    this.ui.proof = false; this.ui.reader = null; this.ui.armed = ''; this.ui.err = ''; this.ui.focusK = '';
    if (step === 'review') this.loadBasket();
    this.render();
    this.scrollToTop();
  };

  P.bind = function () {
    var self = this, r = this.root;
    // "One last read": a click on the dark backdrop around the popup closes it, like "Keep editing"
    // (only when the press also started there, so selecting text and releasing outside does not close it)
    r.addEventListener('mousedown', function (e) { self.ui.downOnBack = !!(e.target.classList && e.target.classList.contains('pf-wrap')); });
    r.addEventListener('click', function (e) {
      if (e.target.classList && e.target.classList.contains('pf-wrap')) {
        if (self.ui.downOnBack !== false && self.ui.proof) { self.ui.proof = false; self.render(); }
        return;
      }
      var el = e.target.closest('[data-act]');
      // inputs and the delivery-country list act on "change" (a click on the list must not redraw it while it opens)
      if (!el || el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'OPTION') return;
      self.act(el.getAttribute('data-act'), el, e);
    });
    r.addEventListener('change', function (e) {
      var el = e.target;
      if (el.tagName === 'INPUT' && el.type === 'checkbox' && el.hasAttribute('data-act')) self.act(el.getAttribute('data-act'), el, e);
      else if (el.tagName === 'SELECT' && el.hasAttribute('data-act')) self.act(el.getAttribute('data-act'), el, e);
    });
    // the field being filled in: the sticky letter preview follows it (see stick)
    r.addEventListener('focusin', function (e) {
      var f = e.target && e.target.getAttribute && e.target.getAttribute('data-f');
      if (!f) return;
      var k = f.split('|')[1];
      if (k !== self.ui.focusK) { self.ui.focusK = k; self.stick(); }
    });
    r.addEventListener('input', function (e) {
      var el = e.target, s = self.set(), v = el.value;
      if (el.hasAttribute('data-f')) {
        var p = el.getAttribute('data-f').split('|');
        self.setVal(s, p[0], p[1], v);
        // new names or grown-ups typed: read one/several from the text again
        if (p[1] === 'names' && s.count) s.count.kids = 'auto';
        if (p[1] === 'grown' && s.count) s.count.grown = 'auto';
        if (el.classList.contains('grow')) { el.style.height = 'auto'; el.style.height = (el.scrollHeight + 2) + 'px'; }
        self.soft();
      } else if (el.hasAttribute('data-e')) {
        var q = el.getAttribute('data-e').split('|'), which = +q[0], k = q[1];
        if (which === 1) { if (k === 'name') s.nameLinked = false; s.env[k] = v; }
        else { s.envLinks[k] = false; s.env2[k] = v; }
        self.soft();
      } else if (el.hasAttribute('data-prompt')) {
        self.ui.prompts[el.getAttribute('data-prompt')] = v;
      } else if (el.hasAttribute('data-pr-text')) {
        var pfx = self.ui.pr && self.ui.pr.fixes[+el.getAttribute('data-pr-text')];
        if (pfx) pfx.draft = v;
      } else if (el.hasAttribute('data-gift')) {
        self.state.gift.note = v;
        self.saveDraft();
      }
    });
    r.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && (self.ui.reader || self.ui.proof)) { self.ui.reader = null; self.ui.proof = false; self.render(); }
      if (e.key === 'Enter' && e.target.hasAttribute && e.target.hasAttribute('data-prompt')) {
        e.preventDefault();
        var b = e.target.parentNode.querySelector('.muse-go');
        if (b) b.click();
      }
    });
  };

  P.act = function (a, el, e) {
    var self = this, st = this.state, s = this.set(), t = this.t(), v = el.getAttribute('data-v');
    var si = el.hasAttribute('data-set') ? +el.getAttribute('data-set') : null;
    switch (a) {
      case 'go': return this.go(el.getAttribute('data-step'));
      case 'lang': st.language = v; return this.render();
      case 'choice': if (s.choice === 'pair' && v !== 'pair') this.unlinkPair(s); s.choice = v; st.active = v === 'pair' ? 'early' : v; return this.render();
      case 'font': st.font = v; return this.render();
      case 'gender': s.gender = v; return this.render();
      case 'count': s.count = s.count || { kids: 'auto', grown: 'auto' }; s.count[el.getAttribute('data-k')] = v; return this.render();
      case 'ltab': st.active = v; return this.render();
      case 'edit-words': {
        var L = this.activeLetter();
        s[L].own = this.plainLetter(s, L);
        if (L === 'early') s.slip = true;
        s.mode[L] = 'own';
        return this.render();
      }
      case 'blank': {
        var L2 = this.activeLetter();
        s[L2].own = '';
        if (L2 === 'early') s.slip = false;
        s.mode[L2] = 'own';
        return this.render();
      }
      case 'santa-words': s.mode[this.activeLetter()] = 'santa'; return this.render();
      case 'keep-elves': s.slip = !!el.checked; return this.render();
      case 'relink': {
        var k = el.getAttribute('data-k'), env = el.getAttribute('data-env');
        if (env === '1') s.nameLinked = true;
        else if (env === '2') s.envLinks[k] = true;
        else s.links[k] = true;
        return this.render();
      }
      case 'muse': { var key = el.getAttribute('data-key'); this.ui.muse[key] = !this.ui.muse[key]; return this.render(); }
      case 'gen': return this.generate(el);
      case 'prev': {
        var i = ORDER.indexOf(st.step);
        if (st.step === 'letters' && s.choice === 'pair' && this.activeLetter() === 'morning') { st.active = 'early'; this.render(); return this.scrollToTop(); }
        if (st.step === 'envelope' && s.choice === 'pair') { st.active = 'morning'; return this.go('letters'); }
        if (st.step === 'review') { st.cur = st.sets.length - 1; }
        return this.go(ORDER[Math.max(0, i - 1)]);
      }
      case 'next': {
        if (st.step === 'letters') {
          if (this.blockedByLength()) return;
          if (s.choice === 'pair' && this.activeLetter() === 'early') { st.active = 'morning'; this.render(); return this.scrollToTop(); }
          return this.go('envelope');
        }
        return this.go(ORDER[ORDER.indexOf(st.step) + 1]);
      }
      case 'reader': this.ui.reader = el.getAttribute('data-kind'); return this.render();
      case 'reader-close': this.ui.reader = null; return this.render();
      case 'reader-zoom': {
        // zoom the full-size letter in or out, keeping the spot that was clicked under the pointer
        var ov = this.root.querySelector('.reader'), box = ov && ov.querySelector('.reader-box.is-letter'), zb = ov && ov.querySelector('.reader-zoom');
        if (!box) return;
        var br = box.getBoundingClientRect(), onBox = el === box && e && e.clientX != null;
        var px = onBox ? e.clientX : window.innerWidth / 2, py = onBox ? e.clientY : window.innerHeight / 3;
        var fx = (px - br.left) / br.width, fy = (py - br.top) / br.height;
        var on = !box.classList.contains('zoomed');
        box.classList.toggle('zoomed', on);
        if (zb) { zb.classList.toggle('on', on); zb.setAttribute('aria-pressed', on ? 'true' : 'false'); }
        var nr = box.getBoundingClientRect();
        ov.scrollLeft += (nr.left + fx * nr.width) - px;
        ov.scrollTop += (nr.top + fy * nr.height) - py;
        return;
      }
      case 'differ': s.envDiffer = !s.envDiffer; return this.render();
      case 'edit-letter': st.cur = si; st.active = v; this.ui.proof = false; return this.go('letters');
      case 'edit-env': st.cur = si; this.ui.proof = false; return this.go('envelope');
      case 'goto': st.cur = si; st.active = v; this.ui.proof = false; return this.go(el.getAttribute('data-step'));
      case 'upsell': st.cur = si; st.sets[si].choice = 'pair'; st.active = v; return this.go('letters');
      case 'rm-letter': {
        var ak = si + '-' + v, set = st.sets[si];
        if (this.ui.armed !== ak) { this.ui.armed = ak; return this.render(); }
        this.ui.armed = '';
        if (set.choice === 'pair') { this.unlinkPair(set); set.choice = v === 'early' ? 'morning' : 'early'; st.active = set.choice; return this.render(); }
        return this.removeSet(si);
      }
      case 'rm-set': {
        if (this.ui.armed !== si + '-set') { this.ui.armed = si + '-set'; return this.render(); }
        this.ui.armed = '';
        return this.removeSet(si);
      }
      case 'sibling': {
        var base = st.sets[st.sets.length - 1], n = newSet();
        n.choice = base.choice; n.gender = 'masc';
        ['grown', 'location', 'way', 'treat', 'hazard', 'habit'].forEach(function (k) { n.early[k] = self.val(base, 'early', k); });
        ['grown', 'way', 'treat', 'place'].forEach(function (k) { n.morning[k] = self.val(base, 'morning', k) || n.morning[k]; });
        n.links = { names: true, grown: true, way: true, treat: true };
        n.env.addr = base.env.addr; n.env.city = base.env.city;
        st.sets.push(n); st.cur = st.sets.length - 1; st.active = this.picked(n)[0];
        return this.go('letters');
      }
      case 'rm-basket': {
        var bk = el.getAttribute('data-key');
        if (this.ui.armed !== 'bk-' + bk) { this.ui.armed = 'bk-' + bk; return this.render(); }
        this.ui.armed = '';
        el.disabled = true;
        return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: bk, quantity: 0 }) })
          .then(function () { self.ui.basket = self.ui.basket.filter(function (x) { return x.key !== bk; }); self.render(); self.bumpCartCount(); });
      }
      case 'gift': st.gift.on = !!el.checked; return this.render();
      case 'proof': this.ui.proof = true; this.ui.confirm = false; this.ui.err = ''; this.ui.pr = null; return this.render();
      case 'pr-run': return this.prRun();
      case 'pr-use': return this.prUse(+v);
      case 'pr-keep': if (this.ui.pr) { this.ui.pr.fixes.splice(+v, 1); this.ui.pr.found = true; } return this.render();
      case 'pr-edit': case 'pr-cancel':
        var pf = this.ui.pr && this.ui.pr.fixes[+v];
        if (!pf) return;
        pf.editing = a === 'pr-edit';
        if (a === 'pr-cancel') pf.draft = null;
        this.render();
        var ta = a === 'pr-edit' && this.root.querySelector('[data-pr-text="' + v + '"]');
        if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); }
        return;
      case 'pr-save':
        var sf = this.ui.pr && this.ui.pr.fixes[+v], tx = this.root.querySelector('[data-pr-text="' + v + '"]');
        if (sf && tx && trim(tx.value)) this.prApply(+v, tx.value);
        return;
      case 'ob-country': this.ui.obCountry = el.value; return this.render();
      case 'proof-close': this.ui.proof = false; return this.render();
      case 'confirm': this.ui.confirm = !!el.checked; return this.render();
      case 'add': return this.addToCart();
    }
  };

  // Letter II stops following Letter I: copy what it was showing into it
  P.unlinkPair = function (s) {
    Object.keys(s.links).forEach(function (k) { if (s.links[k]) { s.morning[k] = s.early[k] || s.morning[k]; } });
  };
  P.removeSet = function (si) {
    var st = this.state, set = st.sets[si], self = this;
    var done = function () {
      st.sets.splice(si, 1);
      if (!st.sets.length) { st.sets = [newSet()]; st.cur = 0; st.active = 'early'; return self.go('letters'); }
      st.cur = Math.min(st.cur, st.sets.length - 1);
      self.render();
    };
    if (set.editKey) {
      fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: set.editKey, quantity: 0 }) })
        .then(function () { self.bumpCartCount(); done(); }).catch(done);
    } else done();
  };
  P.bumpCartCount = function () {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      document.querySelectorAll('.bag .n').forEach(function (n) { n.textContent = c.item_count; });
    }).catch(function () {});
  };

  // ---------- writing helper ----------
  P.generate = function (el) {
    var self = this, s = this.set(), key = el.getAttribute('data-key'), cfg = el.getAttribute('data-cfg');
    var L = el.getAttribute('data-l') || this.activeLetter(), k = el.getAttribute('data-k'), env = el.getAttribute('data-env');
    if (this.ui.gen) return;
    var worker = trim(CFG.worker);
    this.ui.gen = key; this.ui.aiErr[key] = false; this.render();
    var gl = env ? this.picked(s)[0] : L, g = this.gram(s, gl);
    var avoid = [];
    if (k === 'growth') avoid.push(clean(s.early.milestone));
    if (k === 'milestone' && s.choice === 'pair') avoid.push(clean(s.morning.growth));
    var body = {
      letter: env === '2' ? 'morning' : gl, field: cfg === 'address' ? 'address' : k, language: this.L(),
      children: { count: g.sib ? 'multiple' : 'one', gender: this.L() === 'english' ? null : s.gender },
      grownups: { count: g.many ? 'multiple' : 'one' },
      notes: trim(this.ui.prompts[key]).slice(0, 300),
      sentence: cfg === 'address' || k === 'own' ? '' : this.sentenceFor(s, gl, k),
      avoid: avoid.filter(Boolean)
    };
    if (k === 'own') {
      body.greeting = this.greetingLine(s, L, 'NAME');
      // fill the page: measured on the live preview in the chosen handwriting (room kept for the elves' note and the P.S.)
      var words = this.pageWords(s, L);
      if (!words) {
        words = { english: 300, french: 275, german: 255 }[this.L()] || 300;
        if (L === 'early' && s.slip) words -= 55;
        if (trim(s[L].ps)) words -= 20;
      }
      body.words = words;
    }
    var fail = function () { self.ui.gen = ''; self.ui.aiErr[key] = true; self.render(); };
    if (!worker) return fail();
    fetch(worker.replace(/\/+$/, '') + '/santa', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        var txt = res.ok && res.j && trim(res.j.text);
        if (!txt) return fail();
        if (env) {
          if (env === '1') s.env.addr = txt.slice(0, self.lim('addr'));
          else { s.envLinks.addr = false; s.env2.addr = txt.slice(0, self.lim('addr')); }
        } else if (k === 'own') {
          var nm = clean(self.val(s, L, 'names'));
          txt = txt.replace(/\bNAME\b/g, nm || '…');
          var first = trim(txt.split('\n')[0]);
          if (!/[,:!]$/.test(first) || first.length > 60) txt = self.greetingLine(s, L) + '\n\n' + txt;
          s[L].own = txt;
        } else {
          txt = self.stripEcho(txt, body.sentence);
          self.setVal(s, L, k, txt.slice(0, self.lim(k)));
        }
        self.ui.gen = '';
        self.render();
      }).catch(fail);
  };

  // ---------- cart ----------
  P.printData = function (s) {
    var self = this, out = { language: this.L(), font: this.state.font, choice: s.choice, letters: {}, envelopes: {} };
    this.picked(s).forEach(function (L) {
      var d = self.letterData(s, L), txt = function (segs) { return segs.map(function (x) { return x.t; }).join(''); };
      var e = self.envOf(s, L);
      out.letters[L] = {
        title: self.t().titles[L], greeting: d.greeting ? txt(d.greeting) : '', paragraphs: d.paras.map(txt),
        elves_after_paragraph: d.slip ? d.slipAt : null, elves: d.slip ? d.slip.map(function (x) { return [x.label, x.v]; }) : null, 
        closing: d.closing, signature: d.sign, ps: d.ps ? txt(d.ps) : ''
      };
      out.envelopes[L] = { name: e.phName ? '' : e.name, lines: e.phLines ? [] : e.lines };
    });
    return out;
  };
  P.cartItem = function (s) {
    var self = this, t = this.t(), p = this.picked(s), lang = LANGS.filter(function (l) { return l[0] === self.L(); })[0];
    var e1 = this.envOf(s, p[0]), props = {};
    props[t.p_letters] = t.titles[s.choice];
    props[t.p_for] = this.childNames(s);
    props[t.p_lang] = lang ? lang[1] : '';
    props[t.p_hand] = this.state.font === 'script' ? t.hand_script : t.hand_classic;
    props[t.p_env] = [e1.name].concat(e1.lines).join(' · ');
    if (s.choice === 'pair' && s.envDiffer) { var e2 = this.envOf(s, 'morning'); props[t.p_env2] = [e2.name].concat(e2.lines).join(' · '); }
    p.forEach(function (L) { props['_' + t.kicker[L]] = self.letterText(s, L); });
    props._print = JSON.stringify(this.printData(s));
    var keep = clone(s); keep.editKey = null;
    props._dml_state = JSON.stringify({ v: 1, product: 'santa', language: this.L(), font: this.state.font, set: keep });
    var id = CFG.variants && CFG.variants[s.choice];
    return { id: Number(id) || id, quantity: 1, properties: props };
  };
  P.addToCart = function () {
    var self = this, st = this.state, t = this.t();
    if (this.ui.adding) return;
    this.ui.adding = true; this.ui.err = ''; this.render();
    var items = st.sets.map(function (s) { return self.cartItem(s); });
    var removeOld = st.sets.filter(function (s) { return s.editKey; }).reduce(function (chain, s) {
      return chain.then(function () { return fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: s.editKey, quantity: 0 }) }); });
    }, Promise.resolve());
    removeOld.then(function () {
      return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) });
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (j) { throw new Error((j && (j.description || j.message)) || 'cart'); });
      return fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { Gift: st.gift.on ? 'Yes, send to the recipient (no prices in the parcel)' : '', 'Gift note': st.gift.on ? trim(st.gift.note) : '' } }) }).catch(function () {});
    }).then(function () {
      // every letter on the review (the one from the cart and the ones being written) is now in the cart
      self.clearDraft();
      window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/') + 'cart';
    }).catch(function (err) {
      console.error('[DML Santa] add to cart failed', err, items);
      self.ui.adding = false; self.ui.err = t.cart_err; self.render();
    });
  };

  // =====================================================================
  // Phones: a full-screen writing mode, one question at a time
  // =====================================================================
  // On screens up to 760px wide the builder starts as a small card on the page ("Start the letter").
  // Tapping it opens the builder full screen: a slim bar on top (back, step, progress), the letter or
  // envelope preview in the middle, and a card at the bottom with one question, Back and Next.
  // The cards are made from the panel the builder already renders (render.js): nothing about the
  // letter logic changes, only which parts of the panel show at a time. The review step scrolls as
  // one page. Leaving full screen (top-left arrow or the phone's back button) keeps everything typed.
  // Also here, for every screen size: the short explanations under each question sit behind a
  // lightbulb next to its title (P.tipBtn / P.tipHTML).
  var PH = {
    english: {
      start: 'Start the letter', resume: 'Continue the letter', start_note: 'About five minutes. You see the letter take shape as you write.',
      start_title: 'Their letter from Santa', exit: 'Back to the page', next: 'Next', skip: 'Skip', back: 'Back',
      detail: function (i, n) { return 'Detail ' + i + ' of ' + n; }, need: 'Please add this to continue.',
      read: 'Read it full screen', tip: 'Show a tip', lang: 'Language',
      up: function (t, p) { return '+ Add ' + t + ' · ' + p; },
      up_s: function (t, p) { return '+ ' + t + ' · ' + p; }, pr_of: function (i, n) { return i + ' of ' + n; }, pr_accept: 'Accept',
      for_: function (n) { return 'For ' + n; }, whole: 'See the whole letter', whole_s: 'Hide', back_q: 'Back to the question',
      up_l: function (t, p) { return '+ Add the ' + t + ' letter · ' + p; }, resume_n: function (n) { return 'Continue ' + n + '’s letter'; }, resume_nn: 'Continue the letters'
    },
    french: {
      start: 'Commencer la lettre', resume: 'Reprendre la lettre', start_note: 'Environ cinq minutes. La lettre prend forme sous vos yeux.',
      start_title: 'Leur lettre du Père Noël', exit: 'Retour à la page', next: 'Suivant', skip: 'Passer', back: 'Retour',
      detail: function (i, n) { return 'Détail ' + i + ' sur ' + n; }, need: 'Merci de remplir ce champ pour continuer.',
      read: 'Lire en plein écran', tip: 'Afficher une astuce', lang: 'Langue',
      up: function (t, p) { return '+ Ajouter ' + t.toLowerCase() + ' · ' + p; },
      up_s: function (t, p) { return '+ ' + t + ' · ' + p; }, pr_of: function (i, n) { return i + ' sur ' + n; }, pr_accept: 'Accepter',
      for_: function (n) { return 'Pour ' + n; }, whole: 'Voir toute la lettre', whole_s: 'Masquer', back_q: 'Revenir à la question',
      up_l: function (t, p) { return '+ Ajouter la lettre « ' + t + ' » · ' + p; }, resume_n: function (n) { return 'Reprendre la lettre pour ' + n; }, resume_nn: 'Reprendre les lettres'
    },
    german: {
      start: 'Brief beginnen', resume: 'Brief fortsetzen', start_note: 'Etwa fünf Minuten. Du siehst den Brief beim Schreiben entstehen.',
      start_title: 'Ihr Brief vom Weihnachtsmann', exit: 'Zurück zur Seite', next: 'Weiter', skip: 'Überspringen', back: 'Zurück',
      detail: function (i, n) { return 'Detail ' + i + ' von ' + n; }, need: 'Bitte fülle das aus, um weiterzumachen.',
      read: 'Im Vollbild lesen', tip: 'Tipp anzeigen', lang: 'Sprache',
      up: function (t, p) { return '+ ' + t.replace(/^Der /, 'Den ') + ' hinzufügen · ' + p; },
      up_s: function (t, p) { return '+ ' + t + ' · ' + p; }, pr_of: function (i, n) { return i + ' von ' + n; }, pr_accept: 'Übernehmen',
      for_: function (n) { return 'Für ' + n; }, whole: 'Den ganzen Brief sehen', whole_s: 'Ausblenden', back_q: 'Zurück zur Frage',
      up_l: function (t, p) { return '+ Brief „' + t + '“ hinzufügen · ' + p; }, resume_n: function (n) { return 'Den Brief für ' + n + ' fortsetzen'; }, resume_nn: 'Die Briefe fortsetzen'
    }
  };
  var BULB = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18h6"></path><path d="M10 21h4"></path><path d="M12 3a6 6 0 0 0-3.6 10.8c.8.6 1.1 1.3 1.1 2.2h5c0-.9.3-1.6 1.1-2.2A6 6 0 0 0 12 3z"></path></svg>';
  var ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7"></path></svg>';
  var PH_MQ = window.matchMedia ? window.matchMedia('(max-width: 760px)') : { matches: false };
  var PH_AUTO = (function () { try { var q = new URLSearchParams(window.location.search); return !!(q.get('santa_from') || q.get('dml_edit_key') || q.get('write')); } catch (e) { return false; } })();

  P.ph = function () { return PH[this.L()] || PH.english; };
  // the theme editor keeps its own history for the preview frame: no history entries there
  var PH_HIST = !(window.Shopify && window.Shopify.designMode);
  var ourState = function () { try { return !!(history.state && history.state.dmlSanta); } catch (e) { return false; } };
  P.phPush = function () {
    this._phPushed = false;
    if (!PH_HIST) return;
    try { history.pushState({ dmlSanta: 1 }, ''); this._phPushed = ourState(); } catch (e) { this._phPushed = false; }
  };
  P.isPhone = function () { return !!PH_MQ.matches; };

  // ---------- lightbulb tips (all screens) ----------
  P.tipBtn = function (id) {
    var open = !!(this.ui.tips && this.ui.tips[id]);
    return '<button type="button" class="bulb' + (open ? ' on' : '') + '" data-act="tip" data-key="' + esc(id) + '" aria-expanded="' + open + '" aria-controls="tip-' + esc(id) + '" title="' + esc(this.ph().tip) + '"><span class="sr">' + esc(this.ph().tip) + '</span>' + BULB + '</button>';
  };
  P.tipHTML = function (id, text, cls) {
    var open = !!(this.ui.tips && this.ui.tips[id]);
    return '<span class="help tip' + (cls ? ' ' + cls : '') + '" id="tip-' + esc(id) + '"' + (open ? '' : ' hidden') + '>' + esc(text) + '</span>';
  };
  var _lblRow = P.lblRowInner;
  P.lblRowInner = function (it) {
    // the "from the letter" / "From Letter I" tags: the box already shows the reused words
    var h = _lblRow.call(this, it).replace(/<span class="tag">[^<]*<\/span>/, '');
    if (!it.help) return h;
    // the bulb goes right after the label, before tags and counters
    var cut = h.indexOf('</label>') + 8;
    return h.slice(0, cut) + this.tipBtn(it.id) + h.slice(cut);
  };
  var _fieldHTML = P.fieldHTML;
  // phones' keyboards: capital letters only where a sentence or a name starts
  var CAPS = { names: 'words', grown: 'words', ps: 'sentences', own: 'sentences', name: 'words', addr: 'sentences', city: 'words' };
  var caps = function (h, k) { return h.replace(/<(input|textarea) /, '<$1 autocapitalize="' + (CAPS[k] || 'none') + '" '); };
  // phones: a one-line box becomes a box that wraps and grows, so a long answer (a magical address) stays readable
  // the magical address (all screens): the parent may start a new line, like on a real envelope
  var wraps = function (self, h, multi) {
    if (!multi && !(self.ui.phOn && self.isPhone())) return h;
    return h.replace(/<input type="text" ([^>]*?) value="([^"]*)"([^>]*)>/, function (m, a, v, b) {
      return '<textarea class="grow one' + (multi ? ' multi' : '') + '" rows="1"' + (multi ? '' : ' enterkeyhint="next"') + ' ' + (a + b).replace(/\s*autocomplete="off"/, '') + '>' + v + '</textarea>';
    });
  };
  var _envOf = P.envOf;
  P.envOf = function (s, L) {
    var e = _envOf.call(this, s, L), out = [];
    e.lines.forEach(function (l) { String(l).split(/\n+/).forEach(function (x) { x = trim(x); if (x) out.push(x); }); });
    e.lines = out;
    return e;
  };
  P.fieldHTML = function (it) {
    var h = caps(wraps(this, _fieldHTML.call(this, it)), it.k);
    return it.help ? h.replace('<span class="help">' + esc(it.help) + '</span>', this.tipHTML(it.id, it.help)) : h;
  };
  var _envFieldHTML = P.envFieldHTML;
  P.envFieldHTML = function (it) {
    var h = caps(wraps(this, _envFieldHTML.call(this, it), it.k === 'addr'), it.k);
    return it.help ? h.replace('<span class="help">' + esc(it.help) + '</span>', this.tipHTML(it.id, it.help)) : h;
  };
  // "Want different words?" and the elves' note: their explanation goes behind a bulb too
  var _lettersHTML = P.lettersHTML;
  P.lettersHTML = function () {
    var h = _lettersHTML.call(this), t = this.t(), self = this;
    if (t.words_d) h = h.replace('<p class="wb-t">' + esc(t.words_t) + '</p><p class="wb-d">' + esc(t.words_d) + '</p>',
      '<p class="wb-t">' + esc(t.words_t) + this.tipBtn('words') + '</p>' + this.tipHTML('words', t.words_d, 'wb-d'));
    // the elves' note is dropped entirely (Max, 12 Oct)
    if (t.elves_d) h = h.replace('<p class="elf-note">' + esc(t.elves_d) + '</p>', '');
    return h;
  };

  // ---------- the detail being filled in glows in the preview (darker than the other filled-in words) ----------
  P.hot = function (k) {
    this.ui.hotK = k || '';
    var r = this.root;
    if (!r) return;
    Array.prototype.forEach.call(r.querySelectorAll('#pv .hot'), function (el) { el.classList.remove('hot'); });
    if (!k) return;
    Array.prototype.forEach.call(r.querySelectorAll('#pv [data-k="' + k + '"]'), function (el) { el.classList.add('hot'); });
  };
  P.hotKey = function () {
    if (this.ui.phOn && this.isPhone()) {
      var inp = this.phInput(), f = inp && inp.getAttribute('data-f');
      return f ? f.split('|')[1] : '';
    }
    return this.ui.hotK || '';
  };

  // the big reader keeps the highlights, so parents find their own words quickly
  var _readerHTML = P.readerHTML, _letterFrame = P.letterFrame;
  P.letterFrame = function (s, L, hl, measure) { return _letterFrame.call(this, s, L, hl || !!this._rdHL, measure); };
  P.readerHTML = function () { this._rdHL = true; try { return _readerHTML.call(this); } finally { this._rdHL = false; } };

  // ---------- "One last read" (all screens) ----------
  var _proofHTML = P.proofHTML;
  P.proofHTML = function () {
    var h = _proofHTML.call(this), t = this.t();
    return h ? h.replace('<p>' + esc(t.pf_d) + '</p>', '') : h;
  };
  // the spelling and grammar check: one suggestion at a time, like the question cards
  P.proofreadHTML = function () {
    var t = this.t(), p = this.ph(), pr = this.ui.pr, self = this;
    if (!trim(CFG.worker)) return '';
    if (!pr) return '<div class="pr-box"><button type="button" class="btn btn-invite pr-run" data-act="pr-run">' + esc(t.pr_run) + '</button></div>';
    if (pr.status === 'checking') return '<div class="pr-box"><p class="pr-status">' + esc(t.pr_checking) + '</p></div>';
    if (pr.status === 'error') return '<div class="pr-box"><p class="pr-status">' + esc(t.pr_error) + '</p></div>';
    if (!pr.fixes.length) return '<div class="pr-box"><p class="pr-status ok">' + esc(pr.found ? t.pr_done : t.pr_none) + '</p></div>';
    if (pr.total == null || pr.total < pr.fixes.length) pr.total = pr.fixes.length;
    var f = pr.fixes[0], n = pr.total, at = n - pr.fixes.length + 1, i = 0;
    var dots = n > 1 ? '<span class="pr-dots" aria-hidden="true">' + Array.apply(null, Array(n)).map(function (x, k) { return '<i class="' + (k + 1 < at ? 'done' : k + 1 === at ? 'on' : '') + '"></i>'; }).join('') + '</span>' : '';
    var body = f.editing
      ? '<div class="pr-edit"><textarea class="pr-ta" rows="2" data-pr-text="' + i + '" maxlength="' + self.lim(f.id.split('|')[2]) + '">' + esc(f.draft != null ? f.draft : f.orig) + '</textarea>' +
        '<div class="pr-acts"><button type="button" class="btn btn-invite pr-use" data-act="pr-save" data-v="' + i + '">' + esc(t.pr_save) + '</button><button type="button" class="pr-b" data-act="pr-cancel" data-v="' + i + '">' + esc(t.pr_cancel) + '</button></div></div>'
      : (f.unclear ? '<p class="pr-text">' + esc(f.orig) + '</p>' : '<p class="pr-text">' + self.wordDiff(f.orig, f.text) + '</p><p class="pr-was"><span>' + esc(t.pr_yours) + '</span> ' + esc(f.orig) + '</p>') +
        (f.note ? '<p class="pr-why">' + esc(f.note) + '</p>' : '') +
        '<div class="pr-acts">' + (f.unclear ? '' : '<button type="button" class="btn btn-invite pr-use" data-act="pr-use" data-v="' + i + '">' + esc(p.pr_accept) + '</button>') +
        '<button type="button" class="pr-b" data-act="pr-keep" data-v="' + i + '">' + esc(t.pr_keep) + '</button>' +
        '<button type="button" class="pr-b" data-act="pr-edit" data-v="' + i + '">' + esc(t.pr_edit) + '</button></div>';
    return '<div class="pr-box pr-card"><div class="pr-top"><span class="pr-kick">' + esc(t.pr_run) + ' · ' + esc(p.pr_of(at, n)) + '</span>' + dots + '</div>' +
      '<p class="pr-where">' + esc(f.where) + '</p>' + body + '</div>';
  };

  // ---------- full-screen mode ----------
  P.phOpen = function () {
    if (this.ui.phOn) return;
    this.ui.phOn = true;
    this.phPush();
    document.documentElement.classList.add('dml-ph-lock');
    this.render();
    this.phFocus();
  };
  P.phClose = function (fromHistory) {
    if (!this.ui.phOn) return;
    this.ui.phOn = false;
    this.ui.reader = null; this.ui.proof = false; this.ui.phMin = false; this.classList.remove('ph-min');
    document.documentElement.classList.remove('dml-ph-lock');
    this.render();
    // only step back if the entry on top is still the one we added, so we never leave the page
    if (!fromHistory && this._phPushed && ourState()) { this._phPushed = false; this._phSkipPop = true; try { history.back(); } catch (e) {} }
    this._phPushed = false;
    try { this.scrollIntoView({ block: 'start' }); } catch (e) {}
  };
  P.phViewport = function () {
    var vv = window.visualViewport;
    if (vv && vv.scale > 1.02) return; // pinch-zoomed into the letter: keep the layout still
    var h = vv ? vv.height : window.innerHeight, top = vv ? vv.offsetTop : 0;
    this.style.setProperty('--ph-h', Math.round(h) + 'px');
    // the tallest height seen at this width is the screen without keyboard (Android may shrink the page itself)
    var w = window.innerWidth;
    if (this._phW !== w) { this._phW = w; this._phFull = 0; }
    this._phFull = Math.max(this._phFull || 0, h, window.innerHeight);
    var kb = h < this._phFull * 0.78, was = this.classList.contains('ph-kb');
    this.classList.toggle('ph-kb', kb);
    if (kb !== was && this.ui.phOn && this._phCard) { var self = this; setTimeout(function () { self.phPeek(); }, 30); }
    this.style.setProperty('--ph-top', Math.round(top) + 'px');
  };
  P.phInit = function () {
    var self = this;
    this.ui.tips = this.ui.tips || {};
    this.ui.phIdx = {};
    var onVV = function () { if (self.ui.phOn) { self.phViewport(); self.phKeep(); } };
    if (window.visualViewport) { window.visualViewport.addEventListener('resize', onVV); window.visualViewport.addEventListener('scroll', onVV); }
    window.addEventListener('resize', onVV);
    window.addEventListener('popstate', function () {
      if (self._phSkipPop) { self._phSkipPop = false; return; }
      if (self.ui.phOn && !ourState()) { self._phPushed = false; self.phClose(true); }
    });
    // the big reader: a tap on the dark area around the letter closes it (all screens)
    this.root.addEventListener('click', function (e) {
      var el = e.target;
      if (self.ui.reader && el && el.classList && el.classList.contains('reader')) { self.ui.reader = null; self.render(); }
    });
    // desktop: the detail being typed glows in the letter
    this.root.addEventListener('focusin', function (e) {
      var f = e.target && e.target.getAttribute && e.target.getAttribute('data-f');
      if (f && !(self.ui.phOn && self.isPhone())) self.hot(f.split('|')[1]);
    });
    this.root.addEventListener('focusout', function (e) {
      if (self.ui.phOn && self.isPhone()) return;
      var to = e.relatedTarget;
      if (!(to && to.getAttribute && to.getAttribute('data-f'))) self.hot('');
    });
    // phones: wrapping boxes grow as they fill (the envelope's boxes are not grown by the main code), no line breaks
    this.root.addEventListener('input', function (e) {
      var el = e.target;
      if (!el || !el.classList || !el.classList.contains('one')) return;
      var multi = el.classList.contains('multi'), v = el.value;
      // one-line boxes: no line breaks; the address: at most three lines
      var fixed = multi ? v.replace(/\n{2,}/g, '\n').split('\n').slice(0, 3).join('\n') : v.replace(/\s*\n+\s*/g, ' ');
      if (fixed !== v) {
        el.value = fixed; el.dispatchEvent(new Event('input', { bubbles: true }));
        // a line break typed at the end of a one-line box (some Android keyboards): move on, like Next
        if (!multi && self.ui.phOn && /\n\s*$/.test(v)) self.phMove(1);
        return;
      }
      el.style.height = 'auto'; el.style.height = (el.scrollHeight + 2) + 'px';
      self.phKeep();
    });
    // names (children, grown-ups, envelope): the keyboard capitalises every word, so "and" / "et" / "und" is set back to lower case
    this.root.addEventListener('input', function (e) {
      var el = e.target, k = el && el.getAttribute && (el.getAttribute('data-f') || el.getAttribute('data-e') || '');
      k = k.split('|').pop();
      if (k !== 'names' && k !== 'grown' && k !== 'name') return;
      var v = el.value, fixed = v.replace(/(\S\s+)(And|Et|Und)(?=\s)/g, function (m, a, w) { return a + w.toLowerCase(); });
      if (fixed === v) return;
      var at = el.selectionStart, end = el.selectionEnd;
      el.value = fixed;
      try { el.setSelectionRange(at, end); } catch (x) {}
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }, true);
    // phones: "Skip" turns into "Next" as soon as something is typed (and back when the box is emptied)
    this.root.addEventListener('input', function (e) {
      if (!self.ui.phOn || !self._phCard) return;
      var b = self.shell.querySelector('[data-act="ph-next"]'), em = self.phEmpty(self._phCard);
      if (b && self._phGo) b.textContent = em.optional && em.blank ? self.ph().skip : self._phGo;
    });
    this.root.addEventListener('focusin', function () { if (self.ui.phOn) { setTimeout(function () { self.phKeep(); }, 60); setTimeout(function () { self.phKeep(); }, 400); } });
    // phones: the keyboard's Go / Enter key moves on, like Next (one-line fields only)
    this.root.addEventListener('keydown', function (e) {
      if (!self.ui.phOn || e.key !== 'Enter' || e.isComposing) return;
      var el = e.target;
      if (el && el.matches && el.matches('input[data-f], input[data-e], textarea.one:not(.multi)')) { e.preventDefault(); self.phMove(1); }
    });
    this.root.addEventListener('beforeinput', function (e) {
      if (!self.ui.phOn) return;
      var el = e.target;
      if ((e.inputType === 'insertLineBreak' || e.inputType === 'insertParagraph') && el && el.matches && el.matches('textarea.one:not(.multi)')) { e.preventDefault(); self.phMove(1); }
    });
    var onMQ = function () { if (!self.isPhone() && self.ui.phOn) self.phClose(); else self.render(); };
    if (PH_MQ.addEventListener) PH_MQ.addEventListener('change', onMQ); else if (PH_MQ.addListener) PH_MQ.addListener(onMQ);
    // ?write=1 (the homepage buttons): open the writing desk straight away on phones, then tidy the address bar
    try { var u = new URL(window.location.href); if (u.searchParams.has('write')) { u.searchParams.delete('write'); history.replaceState(history.state, '', u.toString()); } } catch (e) {}
    if (PH_AUTO && this.isPhone()) {
      this.ui.phOn = true;
      this.phPush();
      document.documentElement.classList.add('dml-ph-lock');
    }
  };

  // "Continue Elliot's letter" (one child), "Continue the letters" (siblings), the plain label otherwise
  P.phResume = function () {
    var p = this.ph(), sets = this.state.sets;
    if (sets.length > 1) return p.resume_nn;
    var n = trim(this.childNames(sets[0]) || '');
    return n && n.length <= 24 ? p.resume_n(n) : p.resume;
  };
  // the small card on the page, before the builder opens
  P.phStartHTML = function () {
    var p = this.ph(), s = this.set(), started = this.state.step !== 'language' || !!trim(this.val(s, this.activeLetter(), 'names'));
    return '<div class="ph-start"><p class="ph-start-k">' + esc(p.start_title) + '</p>' +
      '<div class="ph-start-pv" data-act="ph-open">' + this.pairPreviewHTML() + '</div>' +
      '<button type="button" class="btn btn-invite ph-go" data-act="ph-open">' + esc(started ? this.phResume() : p.start) + '</button>' +
      '<p class="ph-start-note">' + esc(p.start_note) + '</p></div>';
  };

  var _render = P.render;
  P.render = function () {
    if (!this._phReady) { this._phReady = true; this.phInit(); }
    var phone = this.isPhone(), on = phone && !!this.ui.phOn;
    this.classList.toggle('ph-on', on);
    this.classList.toggle('ph-phone', phone);
    if (phone && !on && !this.ui.loading) {
      this.skeletons();
      this.shell.innerHTML = this.phStartHTML();
      this.saveDraft();
      return;
    }
    if (on) this.phViewport();
    // same step re-drawn (remove a letter, pick a country, tick the gift box): stay where the parent was
    var mainEl = on && this.shell.querySelector('#main'), keep = mainEl && this._phLastStep === this.state.step ? mainEl.scrollTop : 0;
    // "One last read" redrawn (rewrite it myself, accept, keep): it stays where it was (all screens)
    var popEl = this.ui.proof && this.shell && this.shell.querySelector('.reader.pf-wrap'), popKeep = popEl ? popEl.scrollTop : 0;
    this._phLastStep = this.state.step;
    _render.call(this);
    if (on) this.phCards();
    if (keep) { var m2 = this.shell.querySelector('#main'); if (m2) m2.scrollTop = keep; }
    if (popKeep && this.ui.proof) { var p2 = this.shell.querySelector('.reader.pf-wrap'); if (p2) p2.scrollTop = popKeep; }
    this.hot(this.hotKey());
  };
  var _soft = P.soft;
  P.soft = function () {
    _soft.call(this);
    if (this.ui.phOn && this.isPhone()) this.phPeek();
    this.hot(this.hotKey());
  };

  // ---------- the cards ----------
  // Which parts of the panel make up each card, in order
  P.phCardList = function () {
    var panel = this.shell.querySelector('#main .form-col .panel');
    if (!panel) return null;
    var s = this.set(), cards = [], cur = null, hide = [], held = [], skipNote = false;
    // Letter II of a pair: the letters and the handwriting were chosen on Letter I
    var second = this.state.step === 'letters' && s.choice === 'pair' && this.activeLetter() === 'morning';
    var add = function (els) { cur = els.concat(held); held = []; cards.push(cur); };
    Array.prototype.forEach.call(panel.children, function (c) {
      var cl = c.classList;
      if (c.tagName === 'H2' || (c.tagName === 'P' && cl.contains('sub'))) return;
      if (cl.contains('actions') || cl.contains('ph-kick') || cl.contains('ph-err') || cl.contains('ph-nav') || cl.contains('ph-set') || cl.contains('ph-cards') || cl.contains('ph-grab') || cl.contains('ph-head')) return;
      if (c.id === 'ltabs' || (c.tagName === 'P' && cl.contains('hint'))) { hide.push(c); return; }
      if (cl.contains('tiles')) { if (second) { hide.push(c); skipNote = true; } else add([c]); return; }
      if (c.tagName === 'P' && cl.contains('note')) { if (skipNote || !cur) hide.push(c); else cur.push(c); return; }
      // Letter II of a pair: the handwriting card comes back (one handwriting for both letters), with this letter's "Want different words?"
      if (cl.contains('words-box')) { if (cur) cur.push(c); else add([c]); return; }
      if (c.id === 'gender-slot') { if (cur) cur.push(c); return; }
      if (cl.contains('elf-box') || cl.contains('env-group')) {
        Array.prototype.forEach.call(c.querySelectorAll(':scope > .field'), function (f) { add([c, f]); });
        if (!c.querySelector(':scope > .field')) add([c]);
        return;
      }
      add([c]);
    });
    if (held.length && cur) cur.push.apply(cur, held);
    // the letters' note joins the letters, the gender choice joins the names, "Want different words?"
    // joins the handwriting (on Letter II, the first detail), an envelope's "different address" joins its last field
    return { panel: panel, cards: cards, hide: hide };
  };
  P.phKey = function () { return this.state.step + '|' + this.activeLetter() + '|' + this.state.cur + '|' + (this.set().mode[this.activeLetter()] || ''); };
  P.phCards = function () {
    var self = this, p = this.ph(), t = this.t(), st = this.state;
    // top bar instead of the stepper
    var idx = ['language', 'letters', 'envelope', 'review'].indexOf(st.step);
    var bar = '<div class="ph-bar"><button type="button" class="ph-exit" data-act="ph-exit" aria-label="' + esc(p.exit) + '">' + ARROW + '</button>' +
      '<nav class="ph-steps" aria-label="Steps">' + ['language', 'letters', 'envelope', 'review'].map(function (k, n) {
        return '<button type="button" class="ph-st' + (n === idx ? ' on' : n < idx ? ' done' : '') + '" data-act="go" data-step="' + k + '"' + (n === idx ? ' aria-current="step"' : '') + '>' + esc(t.steps[n]) + '</button>';
      }).join('<span class="ph-sep" aria-hidden="true"></span>') + '</nav>' +
      // the price of this child's letter(s), always in sight (the review shows the total on its button)
      (st.step === 'letters' || st.step === 'envelope' ? '<span class="ph-price">' + esc(this.money(this.price(this.set().choice))) + '</span>' : '<span></span>') + '</div>';
    this.shell.insertAdjacentHTML('afterbegin', bar);
    if (st.step === 'review') return this.phReview();
    var info = this.phCardList();
    if (!info || !info.cards.length) return;
    var key = this.phKey(), n = info.cards.length;
    this._phN = n; this._phNKey = key;
    if (this._phBackwards) { this.ui.phIdx[key] = n - 1; this._phBackwards = false; }
    // "Fill these in first" / a missing detail: open the card that holds it
    var go = this.ui.phGoto;
    if (go) {
      this.ui.phGoto = null;
      var at = this.phFindCard(info.cards, go);
      if (at >= 0) { this.ui.phIdx[key] = at; if (this.ui.phMin) { this.ui.phMin = false; this.classList.remove('ph-min'); } }
    }
    var i = Math.max(0, Math.min(n - 1, this.ui.phIdx[key] || 0));
    this.ui.phIdx[key] = i;
    var all = [];
    info.cards.forEach(function (c) { c.forEach(function (el) { if (all.indexOf(el) < 0) all.push(el); }); });
    all.forEach(function (el) { el.classList.add('ph-x'); });
    info.hide.forEach(function (el) { el.classList.add('ph-x'); });
    info.cards[i].forEach(function (el) { el.classList.remove('ph-x'); });
    // inside a group (the elves' slip, an envelope): only the current field
    info.cards[i].forEach(function (el) {
      if (el.classList.contains('elf-box') || el.classList.contains('env-group')) {
        Array.prototype.forEach.call(el.querySelectorAll(':scope > .field'), function (f) { f.classList.toggle('ph-x', info.cards[i].indexOf(f) < 0); });
      }
    });
    // what kind of card: the letters' kicker, then "Detail 2 of 7"
    var s = this.set(), L = this.activeLetter(), general = info.cards[i].some(function (el) { return el.classList.contains('tiles') || !!el.querySelector('[data-act="font"]'); });
    var kick = st.step === 'language' ? p.lang : st.step === 'envelope' ? t.steps[2] : (s.choice === 'pair' && !general ? t.kicker[L] + ' · ' + t.short[L] : t.steps[1]);
    var sets = st.sets, who = sets.length > 1 ? this.childNames(s) : '';
    who = this.childNames(s);
    // one line: "Letter 2 of 2 · For Mila · Letter I · Detail 3 of 12" (the step's own name is in the top bar)
    var bits = [];
    if (sets.length > 1) bits.push(t.letter_of(st.cur + 1, sets.length));
    if (who) bits.push(p.for_(who));
    if (st.step === 'letters' && s.choice === 'pair' && (!general || L === 'morning')) bits.push(t.kicker[L]);
    if (!bits.length) bits.push(kick);
    if (n > 1) bits.push(p.detail(i + 1, n));
    var mini = !!this.ui.phMin;
    var head = '<div class="ph-head"><p class="ph-kick">' + esc(bits.join(' · ')) + '</p>' +
      '<button type="button" class="ph-grab" data-act="ph-sheet" aria-expanded="' + !mini + '"><span>' + esc(mini ? p.back_q : p.whole_s) + '</span><i></i></button></div>' +
      (n > 1 ? '<div class="ph-cards" role="group">' + info.cards.map(function (c, k) {
        return '<button type="button" class="ph-cd' + (k === i ? ' on' : k < i ? ' done' : '') + '" data-act="ph-card" data-v="' + k + '" aria-label="' + esc(p.detail(k + 1, n)) + '"' + (k === i ? ' aria-current="true"' : '') + '><i></i></button>';
      }).join('') + '</div>' : '');
    info.panel.insertAdjacentHTML('afterbegin', head);
    var empty = this.phEmpty(info.cards[i]), last = i === n - 1;
    this._phGo = last ? (this.shell.querySelector('#main .actions .btn-invite') || {}).textContent || p.next : p.next;
    var nextLabel = empty.optional && empty.blank ? p.skip : this._phGo;
    var first = i === 0 && st.step === 'language';
    info.panel.insertAdjacentHTML('beforeend', '<p class="ph-err" hidden>' + esc(p.need) + '</p><div class="ph-nav">' +
      (first ? '<span></span>' : '<button type="button" class="btn btn-back" data-act="ph-prev">' + esc(p.back) + '</button>') +
      '<button type="button" class="btn btn-invite" data-act="ph-next"' + (last && this.blockedByLength && st.step === 'letters' && this.blockedByLength() ? ' disabled' : '') + '>' + esc(nextLabel) + '</button></div>');
    // the preview: a line under it, then show the detail being filled in
    var pv = this.shell.querySelector('#main .preview-col');
    if (pv && !pv.querySelector('.ph-read') && st.step !== 'language') pv.insertAdjacentHTML('beforeend', '<button type="button" class="ph-read" data-act="reader" data-kind="' + (st.step === 'envelope' ? 'env' : 'letter') + '">' + esc(p.read) + '</button>');
    this._phCard = info.cards[i];
    this.phPeek();
    // the box to type in is in sight straight away (the elves' slip has a heading above it)
    this.phKeep(this.phInput());
  };
  // the selector for a detail named by a "missing" button (letters: data-f, envelope: data-e)
  P.phSel = function (go) {
    var f = go.field || '';
    if (!f) return '';
    if (go.step === 'envelope') return ['[data-e="' + (go.env || 1) + '|' + f + '"]', '[data-e$="|' + f + '"]'];
    return ['[data-f="' + go.L + '|' + f + '"]', '[data-f$="|' + f + '"]'];
  };
  P.phFindCard = function (cards, go) {
    var sels = this.phSel(go);
    if (!sels) return -1;
    for (var q = 0; q < sels.length; q++) {
      for (var k = 0; k < cards.length; k++) {
        var hit = cards[k].some(function (el) { return el.matches && (el.matches(sels[q]) || !!el.querySelector(sels[q])) && !(el.classList.contains('elf-box') || el.classList.contains('env-group')); });
        // inside a group (elves' slip, envelope) each field is its own card: [group, field]
        if (!hit) hit = cards[k].length > 1 && (cards[k][0].classList.contains('elf-box') || cards[k][0].classList.contains('env-group')) && cards[k].slice(1).some(function (el) { return el.matches && (el.matches(sels[q]) || !!el.querySelector(sels[q])); });
        if (hit) return k;
      }
    }
    return -1;
  };
  // the box being typed in stays between the card's top and the Back / Next bar
  P.phKeep = function (el) {
    if (!this.ui.phOn || this.ui.phMin) return;
    el = el || this.root.activeElement;
    var fc = this.shell.querySelector('#main .form-col');
    if (!fc || !el || !fc.contains(el) || !el.matches('input,textarea')) return;
    var nav = fc.querySelector('.ph-nav'), fr = fc.getBoundingClientRect(), er = el.getBoundingClientRect();
    var low = (nav ? nav.getBoundingClientRect().top : fr.bottom) - 10, high = fr.top + 6;
    if (er.bottom > low) fc.scrollTop += er.bottom - low;
    else if (er.top < high) fc.scrollTop -= high - er.top;
  };
  // Is the card's field empty, and may it stay empty?
  // phones, the review: less text on screen, the total on the button, Back / Add as pills like the other cards
  P.phReview = function () {
    var r = this.root, open = !!(this.ui.tips && this.ui.tips.order);
    var head = r.querySelector('.rv-order .rv-head');
    var more = Array.prototype.filter.call(r.querySelectorAll('.rv-order .rv-incl'), function (el) { return !el.classList.contains('rv-ship'); });
    if (head && more.length) {
      head.insertAdjacentHTML('beforeend', this.tipBtn('order'));
      var box = document.createElement('div');
      box.className = 'help tip rv-more'; box.id = 'tip-order'; box.hidden = !open;
      more[0].parentNode.insertBefore(box, more[0]);
      more.forEach(function (el) { box.appendChild(el); });
    }
    // the offer to make it a Pair: one button, no paragraph
    var self = this, t = this.t(), p = this.ph();
    Array.prototype.forEach.call(r.querySelectorAll('#main .upsell'), function (u) {
      var b = u.querySelector('[data-act="upsell"]');
      if (!b) return;
      var set = self.state.sets[+b.getAttribute('data-set')] || self.set(), other = b.getAttribute('data-v');
      b.textContent = p.up_l(t.short[other], self.money(self.price('pair') - self.price(set.choice)));
      b.classList.add('ph-upb');
      // into the letter's own row, just before Remove
      var prev = u.previousElementSibling, card = u.closest('.set-card') || (prev && prev.classList.contains('set-card') ? prev : null);
      var side = card && card.querySelector('.lrow .lr-side'), rm = side && side.querySelector('.lr-remove');
      var row = side && side.closest('.lrow');
      if (row) { var w = document.createElement('div'); w.className = 'lr-up'; w.appendChild(b); var miss = row.querySelector(':scope > .lc-missing'); row.insertBefore(w, miss || null); u.remove(); }
      else { u.classList.add('ph-up'); Array.prototype.forEach.call(u.querySelectorAll('p'), function (x) { x.remove(); }); }
    });
    // the price joins the title; the Edit buttons and Remove share one line under the letter
    Array.prototype.forEach.call(r.querySelectorAll('#main .lrow'), function (row) {
      var info = row.querySelector('.lr-info'), acts = info && info.querySelector(':scope > .lr-acts'), side = row.querySelector(':scope > .lr-side');
      var price = side && side.querySelector('.lc-price'), rm = side && side.querySelector('.lr-remove');
      if (price && info) info.appendChild(price);
      if (!side) return;
      side.classList.add('lr-bar');
      if (acts) side.insertBefore(acts, rm || null);
    });
    var tot = r.querySelector('.order-summary .os-row.tot .v'), btn = r.querySelector('#main .panel.review > .actions .btn-primary');
    if (tot && btn && !btn.querySelector('.ph-tot')) {
      var editing = this.state.sets.some(function (s) { return s.editKey; });
      btn.innerHTML = '<span>' + esc(editing ? t.update : t.add_one) + '</span><span class="ph-tot">· ' + esc(tot.textContent) + '</span>';
    }
  };
  P.phInput = function () {
    var inp = null;
    (this._phCard || []).forEach(function (el) {
      if (inp || !el.querySelector) return;
      if (el.matches && el.matches('[data-f],[data-e]')) { inp = el; return; }
      var all = el.querySelectorAll('[data-f],[data-e]');
      for (var n = 0; n < all.length; n++) { var f = all[n].closest('.field'); if (!f || !f.classList.contains('ph-x')) { inp = all[n]; return; } }
    });
    return inp;
  };
  P.phEmpty = function (els) {
    var s = this.set(), L = this.activeLetter(), self = this, out = { blank: false, optional: true, el: null };
    var miss = this.missingFor(s, L).map(function (m) { return m.field; });
    els.forEach(function (el) {
      var inp = el.querySelector && (el.matches('input,textarea') ? el : el.querySelector('input[data-f],textarea[data-f],input[data-e],textarea[data-e]'));
      if (!inp || out.el) return;
      if (el.classList.contains('elf-box') || el.classList.contains('env-group')) return;
      out.el = inp;
      out.blank = !trim(inp.value);
      if (inp.hasAttribute('data-f')) out.optional = miss.indexOf(inp.getAttribute('data-f').split('|')[1]) < 0;
      else out.optional = inp.getAttribute('data-e').split('|')[1] !== 'name';
    });
    return out;
  };
  // keep the line being filled in visible in the preview
  P.phPeek = function () {
    var col = this.shell.querySelector('#main .preview-col'), card = this._phCard;
    if (!col || !card) return;
    var inp = this.phInput(), f = inp && inp.getAttribute('data-f');
    if (this.state.step === 'envelope') return this.phPeekEnv(col, inp);
    var k = f && f.split('|')[1];
    var mark = k && col.querySelector('#pv [data-k="' + k + '"]');
    if (!mark) { if (this.state.step !== 'letters') col.scrollTop = 0; return; }
    var y = mark.getBoundingClientRect().top - col.getBoundingClientRect().top + col.scrollTop;
    col.scrollTo({ top: Math.max(0, y - col.clientHeight * 0.35), behavior: 'smooth' });
  };
  P.phPeekEnv = function (col, inp) {
    var e = inp && inp.getAttribute('data-e'), which = e ? +e.split('|')[0] : 1;
    var mounts = col.querySelectorAll('#pv .env-mount'), m = mounts[Math.min(mounts.length, which) - 1] || mounts[0];
    var txt = m && (m.querySelector('.env-text') || m);
    if (!txt) return;
    if (col.scrollHeight <= col.clientHeight + 2) { col.scrollTop = 0; return; }
    var r = txt.getBoundingClientRect(), c = col.getBoundingClientRect();
    var y = r.top - c.top + col.scrollTop + r.height / 2 - col.clientHeight / 2;
    col.scrollTop = Math.max(0, Math.min(y, col.scrollHeight - col.clientHeight));
  };
  P.phFocus = function () {
    if (!this._phCard) return;
    var inp = this.phInput();
    if (inp && inp.matches('input,textarea') && trim(inp.value) === '') { try { inp.focus({ preventScroll: true }); } catch (e) {} }
  };
  P.phMove = function (d) {
    // the number of cards as shown (counted before the card's own extras were added)
    var key = this.phKey(), n = this._phNKey === key ? this._phN : 0, i = this.ui.phIdx[key] || 0;
    if (!n) { var info = this.phCardList(); n = info ? info.cards.length : 0; }
    if (d > 0) {
      var e = this.phEmpty(this._phCard || []);
      if (e.blank && !e.optional) {
        var err = this.shell.querySelector('.ph-err');
        if (err) err.hidden = false;
        if (e.el) { e.el.classList.add('ph-need'); try { e.el.focus(); } catch (x) {} }
        return;
      }
      if (i < n - 1) { this.ui.phIdx[key] = i + 1; this.render(); return this.phFocus(); }
      var b = this.shell.querySelector('#main .actions [data-act="next"]');
      if (b && b.disabled) return;
      this.act('next', b || document.createElement('button'));
      this.phFocus();
      return;
    }
    if (i > 0) { this.ui.phIdx[key] = i - 1; this.render(); return; }
    if (this.state.step === 'language') return;
    this._phBackwards = true;
    this.act('prev', document.createElement('button'));
  };

  var _act = P.act;
  P.act = function (a, el, e) {
    switch (a) {
      case 'tip': {
        var k = el.getAttribute('data-key'), box = this.root.getElementById('tip-' + k);
        this.ui.tips[k] = !this.ui.tips[k];
        if (box) box.hidden = !this.ui.tips[k];
        el.classList.toggle('on', this.ui.tips[k]);
        el.setAttribute('aria-expanded', String(this.ui.tips[k]));
        return;
      }
      case 'ph-open': return this.phOpen();
      case 'ph-exit': return this.phClose();
      case 'ph-sheet': this.ui.phMin = !this.ui.phMin; this.classList.toggle('ph-min', this.ui.phMin); return this.render();
      case 'ph-card': this.ui.phIdx[this.phKey()] = +el.getAttribute('data-v'); this.render(); return this.phFocus();
      case 'ph-lang': this.ui.phIdx[this.phKey().replace(/^[^|]*/, 'language')] = 0; return this.go('language');
      case 'goto': {
        var go = { L: el.getAttribute('data-v'), step: el.getAttribute('data-step'), field: el.getAttribute('data-field') || '' };
        var sv = this.state.sets[+el.getAttribute('data-set')];
        go.env = sv && sv.choice === 'pair' && go.L === 'morning' ? 2 : 1;
        var phone = this.ui.phOn && this.isPhone();
        if (phone) this.ui.phGoto = go;
        var res = _act.call(this, a, el, e);
        if (phone) { this.phFocus(); return res; }
        // larger screens: the box is brought into view and ready to type in
        var sels = this.phSel(go), box = null;
        for (var q = 0; sels && q < sels.length && !box; q++) box = this.shell.querySelector('#main ' + sels[q]);
        if (box) { try { box.scrollIntoView({ block: 'center' }); box.focus({ preventScroll: true }); } catch (x) {} }
        return res;
      }
      case 'ph-next': return this.phMove(1);
      case 'ph-prev': return this.phMove(-1);
    }
    var r = _act.call(this, a, el, e);
    // phones: picking the language moves on to the letter
    if (a === 'lang' && this.ui.phOn && this.isPhone()) this.go('letters');
    return r;
  };
  // full screen: steps change without scrolling the page
  var _scrollTop = P.scrollToTop;
  P.scrollToTop = function () {
    if (this.ui.phOn && this.isPhone()) { var m = this.shell.querySelector('#main'); if (m) m.scrollTop = 0; return; }
    return _scrollTop.call(this);
  };

  if (!customElements.get('dml-santa-builder')) customElements.define('dml-santa-builder', SantaBuilder);

  // =====================================================================
  // Spelling and grammar check (final read)
  // =====================================================================
  P.prItems = function () {
    var self = this, t = this.t(), items = [], names = [];
    this.state.sets.forEach(function (s, si) {
      self.picked(s).forEach(function (L) {
        var title = t.titles[L] + (self.state.sets.length > 1 ? ' · ' + (self.childNames(s) || t.unnamed) : '');
        var nm = trim(self.val(s, L, 'names')); if (nm) names.push(nm);
        if (s.mode[L] === 'own') {
          var own = trim(s[L].own);
          if (own.length > 1) items.push({ id: si + '|' + L + '|own', text: s[L].own, where: title });
          return;
        }
        LETTER_FIELDS[L].forEach(function (k) {
          if (k === 'names' || k === 'own') return;
          if (L === 'morning' && s.choice === 'pair' && s.links[k] !== undefined && s.links[k]) return; // same text as Letter I
          var v = self.val(s, L, k);
          if (trim(v).length < 2) return;
          var lab = (self.fl(k) || [k])[0];
          items.push({ id: si + '|' + L + '|' + k, text: v, sentence: k === 'ps' ? '' : self.sentenceFor(s, L, k), where: title + ' · ' + lab });
        });
      });
    });
    return { items: items.slice(0, 30), names: names.slice(0, 12) };
  };
  P.prRun = function () {
    var self = this, worker = trim(CFG.worker), q = this.prItems();
    if (!worker) return;
    if (!q.items.length) { this.ui.pr = { status: 'done', fixes: [] }; return this.render(); }
    this.ui.pr = { status: 'checking', fixes: [] };
    this.render();
    var byId = {};
    q.items.forEach(function (x) { byId[x.id] = x; });
    fetch(worker.replace(/\/+$/, '') + '/proofread', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: this.L(), items: q.items.map(function (x) { return { id: x.id, text: x.text, sentence: x.sentence || '' }; }), names: q.names })
    }).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
      if (!d || !Array.isArray(d.fixes)) throw new Error('bad');
      var fixes = [];
      d.fixes.forEach(function (f) {
        var it = byId[f && f.id];
        if (!it || typeof f.suggestion !== 'string') return;
        // unclear: the check cannot tell what was meant (no suggestion, only a note and "Rewrite it myself")
        var unclear = !!f.unclear || !trim(f.suggestion);
        if (unclear ? !trim(String(f.note || '')) : f.suggestion === it.text) return;
        fixes.push({ id: it.id, orig: it.text, text: unclear ? it.text : f.suggestion, unclear: unclear, note: String(f.note || '').slice(0, 160), where: it.where });
      });
      self.ui.pr = { status: 'done', fixes: fixes };
      if (self.ui.proof) self.render();
    }).catch(function () {
      self.ui.pr = { status: 'error', fixes: [] };
      if (self.ui.proof) self.render();
    });
  };
  P.prUse = function (i) { var f = this.ui.pr && this.ui.pr.fixes[i]; if (f) this.prApply(i, f.text); };
  // put a text (the suggestion, or the parent's own rewrite) into the field, if the field has not changed since the check
  P.prApply = function (i, text) {
    var pr = this.ui.pr, f = pr && pr.fixes[i];
    if (!f) return;
    var p = f.id.split('|'), s = this.state.sets[+p[0]], L = p[1], k = p[2];
    if (s) {
      if (k === 'own') { if (s[L].own === f.orig) s[L].own = text; }
      else if (this.val(s, L, k) === f.orig) this.setVal(s, L, k, text);
    }
    pr.fixes.splice(i, 1); pr.found = true;
    this.saveDraft();
    this.render();
  };


})();
